import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { GearSix, Plug, FileText, UserGear, Plus, ArrowsClockwise, Lightning, Trash, Envelope, Clock, Key, PencilSimple, CloudArrowUp, Users, UserPlus, UserMinus, UserCheck, Check, Cloud, SpinnerGap, CheckCircle, XCircle, CaretRight, Timer, ListChecks, Database, ShieldCheck, Cube, MagnifyingGlass, CaretLeft, TreeStructure, DownloadSimple, UploadSimple, Warning, Brain, ChartBar, ClipboardText } from '@phosphor-icons/react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

export default function AdminSettingsPage() {
  return (
    <div data-testid="admin-settings-page" className="p-6 md:p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Admin Settings</h1>
        <p className="text-sm text-navy-500 mt-1">Configure connector, view audit trail, manage admin roles</p>
      </div>

      <Tabs defaultValue="sources" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm p-0.5 h-auto flex-wrap">
          <TabsTrigger value="sources" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-sources">
            <TreeStructure size={14} className="mr-1.5" /> Sources
          </TabsTrigger>
          <TabsTrigger value="provisioning" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-provisioning">
            <Cloud size={14} className="mr-1.5" /> Provisioning
          </TabsTrigger>
          <TabsTrigger value="sso" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-sso">
            <Key size={14} className="mr-1.5" /> SSO
          </TabsTrigger>
          <TabsTrigger value="email" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-email">
            <Envelope size={14} className="mr-1.5" /> Email / SMTP
          </TabsTrigger>
          <TabsTrigger value="cron" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-cron">
            <Clock size={14} className="mr-1.5" /> Scheduled Tasks
          </TabsTrigger>
          <TabsTrigger value="audit" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-audit">
            <FileText size={14} className="mr-1.5" /> Audit Trail
          </TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-roles">
            <UserGear size={14} className="mr-1.5" /> Admin Roles
          </TabsTrigger>
          <TabsTrigger value="features" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-features">
            <Lightning size={14} className="mr-1.5" /> Features
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sources"><SourcesTab /></TabsContent>
        <TabsContent value="provisioning"><ProvisioningTab /></TabsContent>
        <TabsContent value="sso"><SsoTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="cron"><CronTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="roles"><AdminRolesTab /></TabsContent>
        <TabsContent value="features"><FeaturesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SourcesTab() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState(null);
  const [detailTab, setDetailTab] = useState('accounts');
  const [detailData, setDetailData] = useState({ items: [], total: 0 });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailPage, setDetailPage] = useState(0);
  const DETAIL_LIMIT = 25;

  // Azure connector states (kept from original ConnectorTab)
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ tenant_id: '', client_id: '', client_secret: '', redirect_uri: '', access_mode: 'read_write', authoritative: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [aggregating, setAggregating] = useState(false);
  const [aggregationJob, setAggregationJob] = useState(null);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastCompletedJob, setLastCompletedJob] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [showTestResults, setShowTestResults] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // Add/Edit/Delete source states
  const [showAddSource, setShowAddSource] = useState(false);
  const [showEditSource, setShowEditSource] = useState(false);
  const [sourceForm, setSourceForm] = useState({ name: '', description: '', owner: '', owner_email: '' });
  const [savingSource, setSavingSource] = useState(false);
  const [deletingSource, setDeletingSource] = useState(false);
  const [testingSource, setTestingSource] = useState(false);
  const [sourceTestResults, setSourceTestResults] = useState(null);
  const [showSourceTestResults, setShowSourceTestResults] = useState(false);

  // REST API Connector states
  const [showRestConnectorDialog, setShowRestConnectorDialog] = useState(false);
  const [restConnectorStep, setRestConnectorStep] = useState(1);
  const [savingConnector, setSavingConnector] = useState(false);
  const [aggregatingRest, setAggregatingRest] = useState(false);
  const [restForm, setRestForm] = useState({
    name: '', description: '', base_url: '',
    auth: { method: 'bearer', token_url: '', client_id: '', client_secret: '', scope: '', api_key: '', api_key_header: 'Authorization', api_key_prefix: 'Api-Key', bearer_token: '', username: '', password: '' },
    access_mode: 'read_only',
    authoritative: false,
    endpoints: {
      accounts: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', display_name: '', email: '', status: '', department: '' } },
      entitlements: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { entitlement_id: '', name: '', description: '', type: '' } },
      assignments: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', entitlement_id: '' } }
    },
    provisioning: { grant_url: '', grant_method: 'POST', grant_body_template: '', revoke_url: '', revoke_method: 'DELETE', revoke_body_template: '' }
  });

  // Additional Azure AD Connector states
  const [showAzureConnectorDialog, setShowAzureConnectorDialog] = useState(false);
  const [savingAzureConnector, setSavingAzureConnector] = useState(false);
  const [azureForm, setAzureForm] = useState({
    name: '', description: '', tenant_id: '', client_id: '', client_secret: '', redirect_uri: '',
    access_mode: 'read_write', authoritative: true
  });

  // CSV Import states
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Reset Data states
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resettingData, setResettingData] = useState(false);

  const loadSources = useCallback(async () => {
    try {
      const res = await API.get('/admin/sources');
      setSources(res.data.items);
    } catch { toast.error('Failed to load sources'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  // Load Azure connector config
  useEffect(() => {
    API.get('/admin/connector').then(r => {
      setConfig(r.data);
      setForm({ tenant_id: r.data.tenant_id || '', client_id: r.data.client_id || '', client_secret: '', redirect_uri: r.data.redirect_uri || '', access_mode: r.data.access_mode || 'read_write', authoritative: r.data.authoritative !== undefined ? r.data.authoritative : true });
    }).catch(() => {});
    API.get('/admin/connector/aggregate/status').then(r => {
      if (r.data?.status === 'completed' && r.data?.stats) setLastCompletedJob(r.data);
      else if (r.data?.status === 'running') { setAggregating(true); setAggregationJob(r.data); }
    }).catch(() => {});
  }, []);

  // Load detail data when source or tab changes
  const loadDetails = useCallback(async () => {
    if (!selectedSource) return;
    setDetailLoading(true);
    try {
      const res = await API.get(`/admin/sources/${selectedSource.id}/details`, {
        params: { tab: detailTab, skip: detailPage * DETAIL_LIMIT, limit: DETAIL_LIMIT, search: detailSearch || undefined }
      });
      setDetailData(res.data);
    } catch { toast.error('Failed to load details'); }
    finally { setDetailLoading(false); }
  }, [selectedSource, detailTab, detailPage, detailSearch]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  // Azure connector handlers
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.post('/admin/connector', form);
      setConfig(res.data);
      setForm(f => ({ ...f, client_secret: '' }));
      toast.success('Connector configuration saved');
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const res = await API.post('/admin/connector/test');
      setTestResults(res.data);
      setShowTestResults(true);
      if (res.data.status === 'connected') {
        toast.success('All provisioning tests passed!');
        const configRes = await API.get('/admin/connector');
        setConfig(configRes.data);
      } else { toast.error(res.data.message || 'Connection test failed'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      setTestResults({ status: 'error', message: err.response?.data?.detail || 'Test failed', test_results: { steps: [] } });
      setShowTestResults(true);
    } finally { setTesting(false); }
  };

  const handleAggregate = async () => {
    setAggregating(true);
    setElapsedTime(0);
    setLastCompletedJob(null);
    setAggregationJob({ status: 'starting', progress: 0, phase: 'initializing' });
    const startTime = Date.now();
    const timer = setInterval(() => setElapsedTime(Math.floor((Date.now() - startTime) / 1000)), 1000);
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await API.get('/admin/connector/aggregate/status');
        setAggregationJob(statusRes.data);
        if (statusRes.data.status === 'completed' || statusRes.data.status === 'failed') clearInterval(pollInterval);
      } catch {}
    }, 1000);
    try {
      const res = await API.post('/admin/connector/aggregate');
      const data = res.data;
      clearInterval(timer);
      clearInterval(pollInterval);
      const finalJob = await API.get(`/admin/connector/aggregate/job/${data.job_id}`);
      setAggregationJob(finalJob.data);
      if (finalJob.data?.stats) setLastCompletedJob(finalJob.data);
      toast.success(`Aggregation complete in ${data.duration_seconds}s: ${data.users_imported} new users, ${data.users_updated || 0} updated`);
      const configRes = await API.get('/admin/connector');
      setConfig(configRes.data);
      loadSources();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aggregation failed');
      clearInterval(timer);
      clearInterval(pollInterval);
    } finally { setAggregating(false); }
  };

  const loadChangeLog = async (jobId) => {
    try {
      const res = await API.get(`/admin/connector/aggregate/job/${jobId}/changes`, { params: { limit: 100 } });
      setChangeLog(res.data.changes);
      setShowChangeLog(true);
    } catch { toast.error('Failed to load change log'); }
  };

  // Add Source handler
  const handleAddSource = async () => {
    if (!sourceForm.name.trim()) { toast.error('Source name is required'); return; }
    setSavingSource(true);
    try {
      await API.post('/admin/sources', sourceForm);
      toast.success(`Source "${sourceForm.name}" created`);
      setShowAddSource(false);
      setSourceForm({ name: '', description: '', owner: '', owner_email: '' });
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create source'); }
    finally { setSavingSource(false); }
  };

  // Edit Source handler
  const handleEditSource = async () => {
    if (!selectedSource || !sourceForm.name.trim()) return;
    setSavingSource(true);
    try {
      await API.put(`/admin/sources/${selectedSource.id}`, sourceForm);
      toast.success('Source updated');
      setShowEditSource(false);
      // Refresh source data
      const res = await API.get('/admin/sources');
      setSources(res.data.items);
      const updated = res.data.items.find(s => s.id === selectedSource.id);
      if (updated) setSelectedSource(updated);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update source'); }
    finally { setSavingSource(false); }
  };

  // Delete Source handler
  const handleDeleteSource = async () => {
    if (!selectedSource) return;
    if (!window.confirm(`Delete source "${selectedSource.name}" and all its accounts, entitlements, and assignments? This cannot be undone.`)) return;
    setDeletingSource(true);
    try {
      const res = await API.delete(`/admin/sources/${selectedSource.id}`);
      toast.success(res.data.message);
      setSelectedSource(null);
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete source'); }
    finally { setDeletingSource(false); }
  };

  // Test Source handler
  const handleTestSource = async () => {
    if (!selectedSource) return;
    setTestingSource(true);
    setSourceTestResults(null);
    try {
      const res = await API.post(`/admin/sources/${selectedSource.id}/test`);
      setSourceTestResults(res.data);
      setShowSourceTestResults(true);
      if (res.data.status === 'connected') toast.success('All checks passed');
      else if (res.data.status === 'warning') toast.warning('Checks completed with warnings');
      else toast.error(res.data.message || 'Test failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      setSourceTestResults({ status: 'error', message: err.response?.data?.detail || 'Test failed', test_results: { steps: [] } });
      setShowSourceTestResults(true);
    } finally { setTestingSource(false); }
  };

  // CSV Template download
  const handleDownloadTemplate = async () => {
    try {
      const res = await API.get('/app-accounts/import/template');
      const blob = new Blob([res.data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  // CSV Import
  const handleImportCSV = async () => {
    if (!importFile || !selectedSource?.application_id) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await API.post(`/app-accounts/import/${selectedSource.application_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      toast.success(`Import complete: ${d.accounts_created} accounts, ${d.entitlements_created} entitlements, ${d.assignments_created} assignments created`);
      setShowImportDialog(false);
      setImportFile(null);
      loadSources();
      loadDetails();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  // Open edit source dialog
  const openEditSource = () => {
    if (!selectedSource) return;
    setSourceForm({
      name: selectedSource.name || '',
      description: selectedSource.config_summary?.description || '',
      owner: selectedSource.config_summary?.owner || '',
      owner_email: ''
    });
    setShowEditSource(true);
  };

  // REST Connector handlers
  const handleSaveRestConnector = async () => {
    if (!restForm.name.trim() || !restForm.base_url.trim()) { toast.error('Name and Base URL are required'); return; }
    setSavingConnector(true);
    try {
      // Clean up empty endpoint URLs
      const endpoints = {};
      for (const [key, ep] of Object.entries(restForm.endpoints)) {
        if (ep.url.trim()) {
          endpoints[key] = { ...ep, field_mapping: Object.fromEntries(Object.entries(ep.field_mapping).filter(([, v]) => v.trim())) };
        }
      }
      const payload = {
        name: restForm.name,
        description: restForm.description,
        base_url: restForm.base_url,
        auth: restForm.auth,
        endpoints,
        access_mode: restForm.access_mode,
        authoritative: restForm.authoritative,
      };
      // Add provisioning if configured
      if (restForm.provisioning.grant_url || restForm.provisioning.revoke_url) {
        const prov = { ...restForm.provisioning };
        if (prov.grant_body_template && typeof prov.grant_body_template === 'string') {
          try { prov.grant_body_template = JSON.parse(prov.grant_body_template); } catch { /* keep as string */ }
        }
        if (prov.revoke_body_template && typeof prov.revoke_body_template === 'string') {
          try { prov.revoke_body_template = JSON.parse(prov.revoke_body_template); } catch { /* keep as string */ }
        }
        payload.provisioning = prov;
      }
      // Check if editing existing connector
      if (selectedSource?.rest_connector_id) {
        await API.put(`/rest-connectors/${selectedSource.rest_connector_id}`, payload);
        toast.success('Connector updated');
      } else {
        await API.post('/rest-connectors', payload);
        toast.success('REST API connector created');
      }
      setShowRestConnectorDialog(false);
      setRestConnectorStep(1);
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save connector'); }
    finally { setSavingConnector(false); }
  };

  const handleTestRestConnector = async (connectorId) => {
    if (!connectorId) return;
    setTestingSource(true);
    setSourceTestResults(null);
    try {
      const res = await API.post(`/rest-connectors/${connectorId}/test`);
      setSourceTestResults(res.data);
      setShowSourceTestResults(true);
      if (res.data.status === 'connected') toast.success('All connector tests passed');
      else toast.error(res.data.message || 'Some tests failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      setSourceTestResults({ status: 'error', message: err.response?.data?.detail || 'Test failed', test_results: { steps: [] } });
      setShowSourceTestResults(true);
    } finally { setTestingSource(false); }
  };

  const handleAggregateRest = async (connectorId) => {
    if (!connectorId) return;
    setAggregatingRest(true);
    try {
      const res = await API.post(`/rest-connectors/${connectorId}/aggregate`);
      const s = res.data.stats;
      toast.success(`Aggregation complete: ${s.accounts_imported} accounts imported, ${s.entitlements_imported} entitlements, ${s.assignments_imported} assignments`);
      loadSources();
      if (selectedSource) loadDetails();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aggregation failed'); }
    finally { setAggregatingRest(false); }
  };

  const handleDeleteRestConnector = async () => {
    if (!selectedSource?.rest_connector_id) return;
    if (!window.confirm(`Delete connector "${selectedSource.name}" and all its data? This cannot be undone.`)) return;
    setDeletingSource(true);
    try {
      await API.delete(`/rest-connectors/${selectedSource.rest_connector_id}`);
      toast.success('Connector deleted');
      setSelectedSource(null);
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete connector'); }
    finally { setDeletingSource(false); }
  };

  const openRestConnectorConfig = (source) => {
    if (source?.rest_connector_id) {
      // Load existing connector config
      API.get(`/rest-connectors/${source.rest_connector_id}`).then(r => {
        const c = r.data;
        setRestForm({
          name: c.name || '', description: c.description || '', base_url: c.base_url || '',
          auth: { method: c.auth?.method || 'bearer', token_url: c.auth?.token_url || '', client_id: c.auth?.client_id || '', client_secret: '', scope: c.auth?.scope || '', api_key: '', api_key_header: c.auth?.api_key_header || 'Authorization', api_key_prefix: c.auth?.api_key_prefix || 'Api-Key', bearer_token: '', username: c.auth?.username || '', password: '' },
          access_mode: c.access_mode || 'read_only',
          authoritative: c.authoritative || false,
          endpoints: {
            accounts: c.endpoints?.accounts || { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', display_name: '', email: '', status: '', department: '' } },
            entitlements: c.endpoints?.entitlements || { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { entitlement_id: '', name: '', description: '', type: '' } },
            assignments: c.endpoints?.assignments || { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', entitlement_id: '' } }
          },
          provisioning: c.provisioning || { grant_url: '', grant_method: 'POST', grant_body_template: '', revoke_url: '', revoke_method: 'DELETE', revoke_body_template: '' }
        });
      }).catch(() => toast.error('Failed to load connector config'));
    }
    setRestConnectorStep(1);
    setShowRestConnectorDialog(true);
  };


  // Additional Azure AD Connector handlers
  const handleSaveAzureConnector = async () => {
    if (!azureForm.name.trim() || !azureForm.tenant_id.trim() || !azureForm.client_id.trim()) {
      toast.error('Name, Tenant ID, and Client ID are required');
      return;
    }
    setSavingAzureConnector(true);
    try {
      if (selectedSource?.azure_connector_id) {
        await API.put(`/admin/azure-connectors/${selectedSource.azure_connector_id}`, azureForm);
        toast.success('Azure connector updated');
      } else {
        await API.post('/admin/azure-connectors', azureForm);
        toast.success('Azure AD connector created');
      }
      setShowAzureConnectorDialog(false);
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save Azure connector'); }
    finally { setSavingAzureConnector(false); }
  };

  const handleTestAzureConnector = async (connectorId) => {
    if (!connectorId) return;
    setTestingSource(true);
    try {
      const res = await API.post(`/admin/azure-connectors/${connectorId}/test`);
      setSourceTestResults(res.data);
      setShowSourceTestResults(true);
      if (res.data.status === 'connected') toast.success('Azure connector tests passed');
      else toast.error(res.data.message || 'Some tests failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTestingSource(false); }
  };

  const handleDeleteAzureConnector = async () => {
    if (!selectedSource?.azure_connector_id) return;
    if (!window.confirm(`Delete Azure connector "${selectedSource.name}" and all its data?`)) return;
    setDeletingSource(true);
    try {
      await API.delete(`/admin/azure-connectors/${selectedSource.azure_connector_id}`);
      toast.success('Azure connector deleted');
      setSelectedSource(null);
      loadSources();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setDeletingSource(false); }
  };

  const openAzureConnectorConfig = (source) => {
    if (source?.azure_connector_id) {
      API.get(`/admin/azure-connectors/${source.azure_connector_id}`).then(r => {
        const c = r.data;
        setAzureForm({
          name: c.name || '', description: c.description || '',
          tenant_id: c.tenant_id || '', client_id: c.client_id || '', client_secret: '', redirect_uri: c.redirect_uri || '',
          access_mode: c.access_mode || 'read_write', authoritative: c.authoritative !== undefined ? c.authoritative : true
        });
      }).catch(() => toast.error('Failed to load connector'));
    } else {
      setAzureForm({ name: '', description: '', tenant_id: '', client_id: '', client_secret: '', redirect_uri: '', access_mode: 'read_write', authoritative: true });
    }
    setShowAzureConnectorDialog(true);
  };

  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleResetData = async () => {
    setResettingData(true);
    try {
      const res = await API.post('/admin/reset-data');
      toast.success(`Data reset complete. ${res.data.total_deleted} records deleted.`);
      setShowResetConfirm(false);
      setResetConfirmText('');
      loadSources(); // Refresh the sources list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset data');
    } finally {
      setResettingData(false);
    }
  };


  const sourceStatusColor = (s) => {
    if (s === 'connected') return 'bg-emerald-100 text-emerald-700';
    if (s === 'configured') return 'bg-blue-100 text-blue-700';
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'error') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-500';
  };

  const sourceIcon = (type) => {
    if (type === 'azure_ad' || type === 'azure_ad_additional') return (
      <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
        <path d="M0 0h10v10H0z" fill="#F25022"/><path d="M11 0h10v10H11z" fill="#7FBA00"/>
        <path d="M0 11h10v10H0z" fill="#00A4EF"/><path d="M11 11h10v10H11z" fill="#FFB900"/>
      </svg>
    );
    if (type === 'rest_api') return <Plug size={20} className="text-teal-500" />;
    if (type === 'manual_app') return <Cube size={20} className="text-purple-500" />;
    return <Database size={20} className="text-navy-500" />;
  };

  const sourceColor = (type) => {
    if (type === 'azure_ad' || type === 'azure_ad_additional') return 'border-l-blue-500';
    if (type === 'rest_api') return 'border-l-teal-500';
    if (type === 'manual_app') return 'border-l-purple-500';
    return 'border-l-slate-400';
  };

  const detailTotalPages = Math.ceil((detailData.total || 0) / DETAIL_LIMIT);

  const openSource = (source) => {
    setSelectedSource(source);
    setDetailTab('accounts');
    setDetailPage(0);
    setDetailSearch('');
    setShowConfig(false);
  };

  // ========== DETAIL VIEW ==========
  if (selectedSource) {
    return (
      <div className="mt-4 space-y-4" data-testid="source-detail-view">
        {/* Header with back button */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedSource(null)} className="rounded-sm h-8 px-2" data-testid="source-back-btn">
            <CaretLeft size={16} />
          </Button>
          <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
            {sourceIcon(selectedSource.type)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{selectedSource.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`text-[10px] ${sourceStatusColor(selectedSource.status)}`}>{selectedSource.status}</Badge>
              <span className="text-xs text-navy-400">{selectedSource.type === 'azure_ad' ? 'Azure AD Connector (Primary)' : selectedSource.type === 'azure_ad_additional' ? 'Azure AD Connector' : selectedSource.type === 'rest_api' ? 'REST API Connector' : selectedSource.type === 'manual_app' ? 'CSV / Manual Application' : 'Locally Created'}</span>
              {selectedSource.type === 'rest_api' && selectedSource.config_summary && (
                <>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.access_mode === 'read_write' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.access_mode === 'read_write' ? 'Read & Write' : 'Read Only'}
                  </Badge>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.authoritative ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.authoritative ? 'Authoritative' : 'Non-Authoritative'}
                  </Badge>
                </>
              )}
              {selectedSource.type === 'azure_ad' && selectedSource.config_summary && (
                <>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.access_mode === 'read_write' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.access_mode === 'read_write' ? 'Read & Write' : 'Read Only'}
                  </Badge>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.authoritative ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.authoritative ? 'Authoritative' : 'Non-Authoritative'}
                  </Badge>
                </>
              )}
              {selectedSource.type === 'azure_ad_additional' && selectedSource.config_summary && (
                <>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.access_mode === 'read_write' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.access_mode === 'read_write' ? 'Read & Write' : 'Read Only'}
                  </Badge>
                  <Badge className={`text-[10px] ${selectedSource.config_summary.authoritative ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-navy-600'}`}>
                    {selectedSource.config_summary.authoritative ? 'Authoritative' : 'Non-Authoritative'}
                  </Badge>
                </>
              )}
            </div>
          </div>
          {selectedSource.type === 'azure_ad' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} className="rounded-sm text-xs" data-testid="toggle-config-btn">
                <GearSix size={14} className="mr-1.5" /> {showConfig ? 'Hide Config' : 'Configure'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleTestSource} disabled={testingSource} className="rounded-sm text-xs" data-testid="test-source-btn">
                <Lightning size={14} className="mr-1.5" /> {testingSource ? 'Testing...' : 'Test'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleAggregate} disabled={aggregating} className="rounded-sm text-xs" data-testid="aggregate-button">
                <ArrowsClockwise size={14} className={`mr-1.5 ${aggregating ? 'animate-spin' : ''}`} /> {aggregating ? 'Aggregating...' : 'Aggregate'}
              </Button>
            </div>
          )}
          {selectedSource.type === 'manual_app' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={openEditSource} className="rounded-sm text-xs" data-testid="configure-source-btn">
                <GearSix size={14} className="mr-1.5" /> Configure
              </Button>
              <Button variant="outline" size="sm" onClick={handleTestSource} disabled={testingSource} className="rounded-sm text-xs" data-testid="test-source-btn">
                <Lightning size={14} className="mr-1.5" /> {testingSource ? 'Testing...' : 'Test'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="rounded-sm text-xs" data-testid="download-template-btn">
                <DownloadSimple size={14} className="mr-1.5" /> Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setImportFile(null); setShowImportDialog(true); }} className="rounded-sm text-xs" data-testid="import-csv-btn">
                <UploadSimple size={14} className="mr-1.5" /> Import CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeleteSource} disabled={deletingSource} className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="delete-source-btn">
                <Trash size={14} className="mr-1.5" /> {deletingSource ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          )}
          {selectedSource.type === 'rest_api' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openRestConnectorConfig(selectedSource)} className="rounded-sm text-xs" data-testid="configure-rest-btn">
                <GearSix size={14} className="mr-1.5" /> Configure
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleTestRestConnector(selectedSource.rest_connector_id)} disabled={testingSource} className="rounded-sm text-xs" data-testid="test-rest-btn">
                <Lightning size={14} className="mr-1.5" /> {testingSource ? 'Testing...' : 'Test'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAggregateRest(selectedSource.rest_connector_id)} disabled={aggregatingRest} className="rounded-sm text-xs" data-testid="aggregate-rest-btn">
                <ArrowsClockwise size={14} className={`mr-1.5 ${aggregatingRest ? 'animate-spin' : ''}`} /> {aggregatingRest ? 'Aggregating...' : 'Aggregate'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeleteRestConnector} disabled={deletingSource} className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="delete-rest-btn">
                <Trash size={14} className="mr-1.5" /> {deletingSource ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          )}
          {selectedSource.type === 'manual' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleTestSource} disabled={testingSource} className="rounded-sm text-xs" data-testid="test-source-btn">
                <Lightning size={14} className="mr-1.5" /> {testingSource ? 'Testing...' : 'Test'}
              </Button>
            </div>
          )}
          {selectedSource.type === 'azure_ad_additional' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openAzureConnectorConfig(selectedSource)} className="rounded-sm text-xs" data-testid="configure-azure-extra-btn">
                <GearSix size={14} className="mr-1.5" /> Configure
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleTestAzureConnector(selectedSource.azure_connector_id)} disabled={testingSource} className="rounded-sm text-xs" data-testid="test-azure-extra-btn">
                <Lightning size={14} className="mr-1.5" /> {testingSource ? 'Testing...' : 'Test'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeleteAzureConnector} disabled={deletingSource} className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="delete-azure-extra-btn">
                <Trash size={14} className="mr-1.5" /> {deletingSource ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          )}
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
            <Users size={18} className="mx-auto text-blue-700 mb-1" />
            <p className="text-2xl font-bold text-navy-900">{selectedSource.accounts}</p>
            <p className="text-[10px] text-navy-500 uppercase tracking-wider">Accounts</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
            <Key size={18} className="mx-auto text-amber-700 mb-1" />
            <p className="text-2xl font-bold text-navy-900">{selectedSource.entitlements}</p>
            <p className="text-[10px] text-navy-500 uppercase tracking-wider">Entitlements</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
            <ShieldCheck size={18} className="mx-auto text-orange-700 mb-1" />
            <p className="text-2xl font-bold text-navy-900">{selectedSource.roles}</p>
            <p className="text-[10px] text-navy-500 uppercase tracking-wider">Roles</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
            <Clock size={18} className="mx-auto text-navy-800 mb-1" />
            <p className="text-sm font-semibold text-navy-900 mt-1">
              {selectedSource.last_aggregation ? new Date(selectedSource.last_aggregation).toLocaleDateString() : 'Never'}
            </p>
            <p className="text-[10px] text-navy-500 uppercase tracking-wider">Last Aggregation</p>
          </div>
        </div>

        {/* Azure AD Config Section (collapsible) */}
        {selectedSource.type === 'azure_ad' && showConfig && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5 space-y-4">
            <h4 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Azure AD Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Tenant ID</Label>
                <Input value={form.tenant_id} onChange={(e) => setForm({...form, tenant_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="connector-tenant-id" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Client ID</Label>
                <Input value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" data-testid="connector-client-id" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Client Secret</Label>
              <Input type="password" value={form.client_secret} onChange={(e) => setForm({...form, client_secret: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder={config?.client_secret_masked || 'Enter secret'} data-testid="connector-client-secret" />
            </div>
            {/* Access Mode & Authoritative */}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Access Mode</Label>
                <Select value={form.access_mode} onValueChange={(v) => setForm({...form, access_mode: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="azure-access-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read_only">Read Only (Aggregation)</SelectItem>
                    <SelectItem value="read_write">Read & Write (Aggregation + Provisioning)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-navy-400 mt-1">{form.access_mode === 'read_only' ? 'Import only — provisioning to Azure AD is blocked.' : 'Import + provision (create users, manage groups) to Azure AD.'}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Identity Authority</Label>
                <div className="mt-2 flex items-center gap-3">
                  <Switch checked={form.authoritative} onCheckedChange={(val) => setForm({...form, authoritative: val})} data-testid="azure-authoritative-toggle" />
                  <span className="text-sm text-navy-700">{form.authoritative ? 'Authoritative' : 'Non-Authoritative'}</span>
                </div>
                <p className="text-[10px] text-navy-400 mt-1">{form.authoritative ? 'Creates & manages identities. Disables identities not in Azure AD.' : 'Creates accounts correlated to existing identities by email.'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs" data-testid="save-connector-button">
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
              <Button onClick={handleTest} disabled={testing} variant="outline" className="rounded-sm text-xs" data-testid="test-connector-button">
                <Lightning size={14} className="mr-1.5" /> {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              {(lastCompletedJob || (aggregationJob?.status === 'completed')) && (
                <Button onClick={() => loadChangeLog((lastCompletedJob || aggregationJob).id)} variant="ghost" size="sm" className="rounded-sm text-xs" data-testid="view-changes-button">
                  <ListChecks size={14} className="mr-1.5" /> View Changes
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Aggregation Progress */}
        {aggregating && aggregationJob && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SpinnerGap size={18} className="animate-spin text-orange-500" />
                <div>
                  <p className="text-sm font-semibold text-navy-900">Aggregation in Progress</p>
                  <p className="text-xs text-navy-500">Phase: {aggregationJob.phase || 'Starting...'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Timer size={14} className="text-navy-400" />
                <span className="font-mono text-navy-600 text-xs">{formatElapsedTime(elapsedTime)}</span>
              </div>
            </div>
            <Progress value={aggregationJob.progress || 0} className="h-2" />
            {aggregationJob.phases && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                {['token', 'users', 'groups', 'memberships', 'provisioning', 'role_sync'].map(phase => {
                  const pd = aggregationJob.phases[phase] || {};
                  const labels = { token: 'Auth Token', users: 'Import Users', groups: 'Import Groups', memberships: 'Sync Memberships', provisioning: 'Provisioning', role_sync: 'Role Sync' };
                  return (
                    <div key={phase} className={`p-2 rounded border ${pd.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : pd.status === 'running' ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center gap-1">
                        {pd.status === 'completed' ? <CheckCircle size={12} className="text-emerald-500" /> : pd.status === 'running' ? <SpinnerGap size={12} className="animate-spin text-orange-500" /> : <Clock size={12} className="text-navy-400" />}
                        <span className="font-medium text-navy-700">{labels[phase]}</span>
                      </div>
                      {pd.total > 0 && <p className="text-[10px] text-navy-500 mt-0.5">{pd.processed}/{pd.total}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Last Aggregation Summary */}
        {!aggregating && lastCompletedJob?.stats && selectedSource.type === 'azure_ad' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4" data-testid="aggregation-summary-card">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle size={18} weight="duotone" className="text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Last Aggregation Completed</p>
                  <p className="text-xs text-emerald-700">{new Date(lastCompletedJob.completed_at).toLocaleString()} &middot; Duration: {lastCompletedJob.duration_seconds}s</p>
                </div>
              </div>
              <Button onClick={() => setLastCompletedJob(null)} variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 h-6 px-2" data-testid="dismiss-summary-button">Dismiss</Button>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3">
              {[
                { label: 'New Users', value: lastCompletedJob.stats.users_imported || 0 },
                { label: 'New Groups', value: lastCompletedJob.stats.groups_imported || 0 },
                { label: 'Memberships', value: lastCompletedJob.stats.memberships_imported || 0 },
                { label: 'Provisioned', value: (lastCompletedJob.stats.provisioned_grants || 0) + (lastCompletedJob.stats.provisioned_revokes || 0) }
              ].map(s => (
                <div key={s.label} className="text-center p-2 bg-white rounded border border-emerald-100">
                  <p className="text-lg font-bold text-navy-900">{s.value}</p>
                  <p className="text-[10px] text-navy-500 uppercase">{s.label}</p>
                </div>
              ))}
            </div>
            {(lastCompletedJob.stats.lifecycle_joiners > 0 || lastCompletedJob.stats.lifecycle_movers > 0 || lastCompletedJob.stats.lifecycle_leavers > 0) && (
              <div className="mt-3 pt-3 border-t border-emerald-200">
                <p className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-1">
                  <Lightning size={12} /> Lifecycle Automations Triggered
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { label: 'Joiners', value: lastCompletedJob.stats.lifecycle_joiners || 0, color: 'text-emerald-700' },
                    { label: 'Movers', value: lastCompletedJob.stats.lifecycle_movers || 0, color: 'text-blue-700' },
                    { label: 'Leavers', value: lastCompletedJob.stats.lifecycle_leavers || 0, color: 'text-red-700' },
                    { label: 'Events', value: lastCompletedJob.stats.lifecycle_events_triggered || 0, color: 'text-amber-700' },
                    { label: 'Actions', value: lastCompletedJob.stats.lifecycle_actions_executed || 0, color: 'text-purple-700' }
                  ].map(s => (
                    <div key={s.label} className="text-center p-1.5 bg-white rounded border border-emerald-100">
                      <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-[9px] text-navy-500 uppercase">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detail data tabs */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex gap-1">
              {['accounts', 'entitlements', 'roles'].map(tab => (
                <button
                  key={tab}
                  onClick={() => { setDetailTab(tab); setDetailPage(0); setDetailSearch(''); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${detailTab === tab ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-slate-100'}`}
                  data-testid={`detail-tab-${tab}`}
                >
                  {tab === 'accounts' && <Users size={12} className="inline mr-1" />}
                  {tab === 'entitlements' && <Key size={12} className="inline mr-1" />}
                  {tab === 'roles' && <ShieldCheck size={12} className="inline mr-1" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} ({detailTab === tab ? detailData.total : '...'})
                </button>
              ))}
            </div>
            <div className="relative w-60">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy-400" />
              <Input
                placeholder={`Search ${detailTab}...`}
                value={detailSearch}
                onChange={(e) => { setDetailSearch(e.target.value); setDetailPage(0); }}
                className="pl-8 h-8 text-xs rounded-sm"
                data-testid="detail-search-input"
              />
            </div>
          </div>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerGap size={24} className="animate-spin text-navy-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {detailTab === 'accounts' && (
                    <>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Name</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Email</TableHead>
                      {selectedSource.type === 'azure_ad' && <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Department</TableHead>}
                      {selectedSource.type === 'azure_ad' && <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Job Title</TableHead>}
                      {selectedSource.type === 'manual_app' && <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Account ID</TableHead>}
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Status</TableHead>
                    </>
                  )}
                  {detailTab === 'entitlements' && (
                    <>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Name</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Type</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Members</TableHead>
                      {selectedSource.type !== 'manual_app' && <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Source</TableHead>}
                    </>
                  )}
                  {detailTab === 'roles' && (
                    <>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Name</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Description</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Members</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Entitlements</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Requestable</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-sm text-navy-400">
                      No {detailTab} found for this source.
                    </TableCell>
                  </TableRow>
                ) : detailData.items.map((item, idx) => (
                  <TableRow key={item.id || idx} className="hover:bg-slate-50 transition-colors">
                    {detailTab === 'accounts' && (
                      <>
                        <TableCell className="text-sm font-medium text-navy-900">{item.display_name || item.account_id || '-'}</TableCell>
                        <TableCell className="text-sm text-navy-600">{item.email || '-'}</TableCell>
                        {selectedSource.type === 'azure_ad' && <TableCell className="text-sm text-navy-600">{item.department || '-'}</TableCell>}
                        {selectedSource.type === 'azure_ad' && <TableCell className="text-sm text-navy-600">{item.job_title || '-'}</TableCell>}
                        {selectedSource.type === 'manual_app' && <TableCell className="text-sm text-navy-600 font-mono">{item.account_id || '-'}</TableCell>}
                        <TableCell>
                          <Badge className={`text-[10px] ${item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-500'}`}>
                            {item.status || 'active'}
                          </Badge>
                        </TableCell>
                      </>
                    )}
                    {detailTab === 'entitlements' && (
                      <>
                        <TableCell className="text-sm font-medium text-navy-900">{item.name}</TableCell>
                        <TableCell><Badge className="text-[10px] bg-slate-100 text-navy-600">{item.type || 'permission'}</Badge></TableCell>
                        <TableCell className="text-sm text-navy-600">{item.member_count ?? '-'}</TableCell>
                        {selectedSource.type !== 'manual_app' && (
                          <TableCell>
                            <Badge className={`text-[10px] ${item.source === 'azure_ad' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                              {item.source || 'manual'}
                            </Badge>
                          </TableCell>
                        )}
                      </>
                    )}
                    {detailTab === 'roles' && (
                      <>
                        <TableCell className="text-sm font-medium text-navy-900">
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck size={14} className="text-orange-500" />
                            {item.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-navy-600 max-w-xs truncate">{item.description || '-'}</TableCell>
                        <TableCell className="text-sm text-navy-600">{item.assignment_count || 0}</TableCell>
                        <TableCell className="text-sm text-navy-600">{(item.entitlements || []).length}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${item.requestable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-500'}`}>
                            {item.requestable ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {detailData.total > DETAIL_LIMIT && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-xs text-navy-500">
                Showing {detailPage * DETAIL_LIMIT + 1} to {Math.min((detailPage + 1) * DETAIL_LIMIT, detailData.total)} of {detailData.total}
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={detailPage === 0} onClick={() => setDetailPage(p => p - 1)} className="rounded-sm h-7 px-2 text-xs">
                  <CaretLeft size={12} className="mr-1" /> Prev
                </Button>
                <Button variant="outline" size="sm" disabled={detailPage >= detailTotalPages - 1} onClick={() => setDetailPage(p => p + 1)} className="rounded-sm h-7 px-2 text-xs">
                  Next <CaretRight size={12} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Azure Config Info */}
        {selectedSource.type === 'azure_ad' && selectedSource.config_summary && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <Plug size={16} className="text-blue-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Bidirectional Sync</h4>
                <p className="text-xs text-blue-700 mt-1">
                  This connector <strong>aggregates users and groups</strong> from Azure AD and <strong>provisions changes back</strong>. For <strong>SSO</strong>, use the SSO tab.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Test Results Dialog */}
        <Dialog open={showTestResults} onOpenChange={setShowTestResults}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {testResults?.status === 'connected' ? <CheckCircle size={20} weight="duotone" className="text-emerald-600" /> : <XCircle size={20} weight="duotone" className="text-red-600" />}
                Connection Test Results
              </DialogTitle>
              <DialogDescription>{testResults?.status === 'connected' ? 'All provisioning operations completed successfully' : 'One or more tests failed'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {testResults?.test_results?.steps?.map((step, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-sm border ${step.status === 'passed' ? 'bg-emerald-50 border-emerald-200' : step.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step.status === 'passed' ? 'bg-emerald-500' : step.status === 'failed' ? 'bg-red-500' : 'bg-slate-400'}`}>
                    {step.status === 'passed' ? <Check size={14} className="text-white" /> : <XCircle size={14} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${step.status === 'passed' ? 'text-emerald-900' : 'text-red-900'}`}>{step.step}</p>
                    {step.details && <p className={`text-xs mt-0.5 ${step.status === 'passed' ? 'text-emerald-700' : 'text-red-700'}`}>{step.details}</p>}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowTestResults(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Log Dialog */}
        <Dialog open={showChangeLog} onOpenChange={setShowChangeLog}>
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ListChecks size={20} className="text-blue-600" /> Aggregation Change Log</DialogTitle>
              <DialogDescription>Detailed log of all changes made during the aggregation</DialogDescription>
            </DialogHeader>
            <div className="max-h-[50vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-bold uppercase w-20">Type</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase w-24">Entity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase">Name</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase">Details</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase w-32">Time</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {changeLog.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-navy-500">No changes recorded</TableCell></TableRow>
                  ) : changeLog.map((change, idx) => (
                    <TableRow key={change.id || `change-${change.timestamp}-${idx}`} className="hover:bg-slate-50">
                      <TableCell><Badge className={`text-[10px] ${change.type === 'created' ? 'bg-emerald-100 text-emerald-700' : change.type === 'updated' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-700'}`}>{change.type}</Badge></TableCell>
                      <TableCell className="text-xs text-navy-600">{change.entity_type}</TableCell>
                      <TableCell className="text-xs font-medium text-navy-900">{change.entity_name}</TableCell>
                      <TableCell className="text-xs text-navy-500">{change.details && Object.entries(change.details).map(([k, v]) => <span key={k} className="mr-2">{k}: {v}</span>)}</TableCell>
                      <TableCell className="text-xs text-navy-400">{new Date(change.timestamp).toLocaleTimeString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowChangeLog(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Source Test Results Dialog */}
        <Dialog open={showSourceTestResults} onOpenChange={setShowSourceTestResults}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {sourceTestResults?.status === 'connected' ? <CheckCircle size={20} weight="duotone" className="text-emerald-600" /> : sourceTestResults?.status === 'warning' ? <Warning size={20} weight="duotone" className="text-amber-600" /> : <XCircle size={20} weight="duotone" className="text-red-600" />}
                Source Test Results
              </DialogTitle>
              <DialogDescription>{sourceTestResults?.message || 'Test completed'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {sourceTestResults?.test_results?.steps?.map((step, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-sm border ${step.status === 'passed' ? 'bg-emerald-50 border-emerald-200' : step.status === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step.status === 'passed' ? 'bg-emerald-500' : step.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`}>
                    {step.status === 'passed' ? <Check size={14} className="text-white" /> : step.status === 'warning' ? <Warning size={14} className="text-white" /> : <XCircle size={14} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${step.status === 'passed' ? 'text-emerald-900' : step.status === 'warning' ? 'text-amber-900' : 'text-red-900'}`}>{step.step}</p>
                    {step.details && <p className={`text-xs mt-0.5 ${step.status === 'passed' ? 'text-emerald-700' : step.status === 'warning' ? 'text-amber-700' : 'text-red-700'}`}>{step.details}</p>}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowSourceTestResults(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Source Dialog */}
        <Dialog open={showEditSource} onOpenChange={setShowEditSource}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Configure Source</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Source Name *</Label>
                <Input value={sourceForm.name} onChange={(e) => setSourceForm({...sourceForm, name: e.target.value})} className="mt-1 rounded-sm" data-testid="edit-source-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
                <Input value={sourceForm.description} onChange={(e) => setSourceForm({...sourceForm, description: e.target.value})} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Owner</Label>
                <Input value={sourceForm.owner} onChange={(e) => setSourceForm({...sourceForm, owner: e.target.value})} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Owner Email</Label>
                <Input value={sourceForm.owner_email} onChange={(e) => setSourceForm({...sourceForm, owner_email: e.target.value})} className="mt-1 rounded-sm" type="email" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditSource(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleEditSource} disabled={savingSource} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-edit-source-btn">
                {savingSource ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import CSV Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <UploadSimple size={18} className="text-purple-500" />
                  Import CSV — {selectedSource?.name}
                </div>
              </DialogTitle>
              <DialogDescription>Upload a CSV file with accounts and entitlements for this source.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-navy-700 mb-1">CSV Format</p>
                <p className="text-[10px] text-navy-500 font-mono">account_id, email, display_name, status, entitlement_name, entitlement_description</p>
                <Button variant="link" size="sm" onClick={handleDownloadTemplate} className="text-xs text-purple-600 p-0 h-auto mt-1" data-testid="import-download-template">
                  <DownloadSimple size={12} className="mr-1" /> Download template
                </Button>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">CSV File *</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="mt-1 rounded-sm text-sm"
                  data-testid="import-file-input"
                />
              </div>
              {importFile && (
                <p className="text-xs text-navy-500">Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImportDialog(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleImportCSV} disabled={!importFile || importing} className="bg-purple-500 hover:bg-purple-600 text-white rounded-sm" data-testid="import-submit-btn">
                {importing ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Importing...</> : <><UploadSimple size={14} className="mr-1.5" /> Import</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== SOURCE LISTING VIEW ==========
  return (
    <div className="mt-4 space-y-4" data-testid="sources-listing">
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <SpinnerGap size={24} className="animate-spin text-navy-400" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-end mb-1">
            <Button onClick={() => { setSourceForm({ name: '', description: '', owner: '', owner_email: '' }); setShowAddSource(true); }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs" data-testid="add-source-btn">
              <Plus size={14} className="mr-1.5" /> Add Source
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {sources.map(source => (
              <div
                key={source.id}
                onClick={() => openSource(source)}
                className={`bg-white border border-slate-200 border-l-4 ${sourceColor(source.type)} rounded-sm shadow-sm p-5 cursor-pointer hover:shadow-md transition-all group`}
                data-testid={`source-card-${source.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-sm bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-slate-100 transition-colors">
                    {sourceIcon(source.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{source.name}</h4>
                      <Badge className={`text-[10px] ${sourceStatusColor(source.status)}`}>{source.status}</Badge>
                    </div>
                    <p className="text-xs text-navy-500 mt-0.5">
                      {source.type === 'azure_ad' && 'Microsoft Azure Active Directory'}
                      {source.type === 'manual_app' && 'CSV / Manual Application'}
                      {source.type === 'manual' && 'Locally created identities and entitlements'}
                    </p>
                  </div>
                  <div className="flex items-center gap-6 shrink-0">
                    <div className="text-center">
                      <p className="text-lg font-bold text-navy-900">{source.accounts}</p>
                      <p className="text-[10px] text-navy-500 uppercase">Accounts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-navy-900">{source.entitlements}</p>
                      <p className="text-[10px] text-navy-500 uppercase">Entitlements</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-navy-900">{source.roles}</p>
                      <p className="text-[10px] text-navy-500 uppercase">Roles</p>
                    </div>
                    <div className="text-center min-w-[80px]">
                      <p className="text-xs font-medium text-navy-700">
                        {source.last_aggregation ? new Date(source.last_aggregation).toLocaleDateString() : '—'}
                      </p>
                      <p className="text-[10px] text-navy-500 uppercase">Last Sync</p>
                    </div>
                    <CaretRight size={16} className="text-navy-300 group-hover:text-orange-500 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {sources.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-sm text-navy-400">
              <TreeStructure size={32} className="mx-auto mb-3 text-navy-300" />
              No data sources configured yet.
            </div>
          )}

          {/* Danger Zone: Reset All Data */}
          <div className="mt-8 border border-red-200 rounded-sm bg-red-50/50">
            <div className="px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-red-100 flex items-center justify-center">
                  <Trash size={20} className="text-red-600" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-red-900">Reset All Data</h4>
                  <p className="text-xs text-red-600 mt-0.5">Delete all identities, roles, entitlements, applications, requests, tickets, audit logs, and reports. Admin account and system settings are preserved.</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(true)}
                className="border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-sm text-xs shrink-0"
                data-testid="reset-data-button"
              >
                <Trash size={14} className="mr-1.5" /> Reset Data
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Reset Data Confirmation Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="max-w-md" aria-describedby="reset-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={20} weight="fill" /> Reset All Data
            </DialogTitle>
            <DialogDescription id="reset-desc">This action is irreversible. All business data will be permanently deleted.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-700">
              <p className="font-semibold mb-1">The following will be deleted:</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>All identities, roles, and entitlements</li>
                <li>All applications and app accounts</li>
                <li>All access requests, approvals, and workflows</li>
                <li>All certifications and SoD policies</li>
                <li>All provisioning tickets and JIT requests</li>
                <li>All audit logs and reports</li>
              </ul>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-700">
              <p className="font-semibold mb-1">The following will be preserved:</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Admin account and user accounts</li>
                <li>SSO, SMTP, and connector configuration</li>
                <li>Feature toggles and preferences</li>
              </ul>
            </div>
            <div>
              <Label className="text-xs font-semibold text-navy-700">Type <span className="font-mono text-red-600">RESET</span> to confirm</Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="Type RESET to confirm"
                className="mt-1 rounded-sm text-sm"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetConfirm(false); setResetConfirmText(''); }} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleResetData}
              disabled={resetConfirmText !== 'RESET' || resettingData}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm disabled:opacity-50"
              data-testid="reset-confirm-button"
            >
              {resettingData ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Resetting...</> : 'Reset All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog — Source Type Selection */}
      <Dialog open={showAddSource} onOpenChange={setShowAddSource}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Plus size={18} className="text-orange-500" />
                Add New Source
              </div>
            </DialogTitle>
            <DialogDescription>Choose a connector type to onboard accounts and entitlements.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-4">
            {/* Azure AD Connector */}
            <button
              onClick={() => {
                setShowAddSource(false);
                setAzureForm({ name: '', description: '', tenant_id: '', client_id: '', client_secret: '', redirect_uri: '', access_mode: 'read_write', authoritative: true });
                setShowAzureConnectorDialog(true);
              }}
              className="group border-2 border-slate-200 hover:border-blue-400 rounded-lg p-5 text-left transition-all hover:shadow-md"
              data-testid="add-source-azure-ad"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                    <path d="M0 0h10v10H0z" fill="#F25022"/><path d="M11 0h10v10H11z" fill="#7FBA00"/>
                    <path d="M0 11h10v10H0z" fill="#00A4EF"/><path d="M11 11h10v10H11z" fill="#FFB900"/>
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-sm text-navy-900">Azure AD</h4>
                </div>
              </div>
              <p className="text-xs text-navy-500">Connect an additional Azure Active Directory tenant. Import users and groups via Microsoft Graph API.</p>
              <div className="flex flex-wrap gap-1 mt-3">
                <Badge className="text-[9px] bg-blue-50 text-blue-700 border border-blue-200">Microsoft Graph</Badge>
                <Badge className="text-[9px] bg-blue-50 text-blue-700 border border-blue-200">OAuth2</Badge>
              </div>
            </button>
            {/* REST API Connector */}
            <button
              onClick={() => {
                setShowAddSource(false);
                setRestForm({
                  name: '', description: '', base_url: '',
                  auth: { method: 'bearer', token_url: '', client_id: '', client_secret: '', scope: '', api_key: '', api_key_header: 'Authorization', api_key_prefix: 'Api-Key', bearer_token: '', username: '', password: '' },
                  access_mode: 'read_only',
                  authoritative: false,
                  endpoints: {
                    accounts: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', display_name: '', email: '', status: '', department: '' } },
                    entitlements: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { entitlement_id: '', name: '', description: '', type: '' } },
                    assignments: { url: '', method: 'GET', response_path: '', pagination: { type: 'none', param: 'offset', limit_param: 'limit', page_size: 100, max_pages: 50 }, field_mapping: { account_id: '', entitlement_id: '' } }
                  },
                  provisioning: { grant_url: '', grant_method: 'POST', grant_body_template: '', revoke_url: '', revoke_method: 'DELETE', revoke_body_template: '' }
                });
                setRestConnectorStep(1);
                setShowRestConnectorDialog(true);
              }}
              className="group border-2 border-slate-200 hover:border-teal-400 rounded-lg p-5 text-left transition-all hover:shadow-md"
              data-testid="add-source-rest-api"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                  <Plug size={22} className="text-teal-600" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-navy-900">REST API Connector</h4>
                </div>
              </div>
              <p className="text-xs text-navy-500">Connect to any application via REST API. Configure authentication, endpoints, and field mappings to import accounts and entitlements automatically.</p>
              <div className="flex flex-wrap gap-1 mt-3">
                {['OAuth2', 'API Key', 'Bearer', 'Basic Auth'].map(a => (
                  <Badge key={a} className="text-[9px] bg-teal-50 text-teal-700 border border-teal-200">{a}</Badge>
                ))}
              </div>
            </button>
            {/* Manual/CSV Source */}
            <button
              onClick={() => {
                setShowAddSource(false);
                setSourceForm({ name: '', description: '', owner: '', owner_email: '' });
                setTimeout(() => setShowEditSource(true), 100);
              }}
              className="group border-2 border-slate-200 hover:border-purple-400 rounded-lg p-5 text-left transition-all hover:shadow-md"
              data-testid="add-source-manual"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                  <Cube size={22} className="text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-navy-900">CSV / Manual Import</h4>
                </div>
              </div>
              <p className="text-xs text-navy-500">Create a source and import accounts and entitlements via CSV file upload. Best for applications without API access.</p>
              <div className="flex flex-wrap gap-1 mt-3">
                <Badge className="text-[9px] bg-purple-50 text-purple-700 border border-purple-200">CSV Upload</Badge>
                <Badge className="text-[9px] bg-purple-50 text-purple-700 border border-purple-200">Manual Entry</Badge>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* REST API Connector Configuration Dialog */}
      <Dialog open={showRestConnectorDialog} onOpenChange={setShowRestConnectorDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Plug size={18} className="text-teal-500" />
                {selectedSource?.rest_connector_id ? 'Edit REST API Connector' : 'New REST API Connector'}
              </div>
            </DialogTitle>
            <DialogDescription>
              Step {restConnectorStep} of 4: {['', 'Connection & Auth', 'Accounts Endpoint', 'Entitlements & Assignments', 'Provisioning (Write-back)'][restConnectorStep]}
            </DialogDescription>
            {/* Step indicator */}
            <div className="flex gap-1 mt-2">
              {[1,2,3,4].map(s => (
                <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= restConnectorStep ? 'bg-teal-500' : 'bg-slate-200'}`} />
              ))}
            </div>
          </DialogHeader>

          {/* Step 1: Connection & Auth */}
          {restConnectorStep === 1 && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Connector Name *</Label>
                <Input value={restForm.name} onChange={(e) => setRestForm({...restForm, name: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., PeopleHR, ServiceNow" data-testid="rest-connector-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
                <Input value={restForm.description} onChange={(e) => setRestForm({...restForm, description: e.target.value})} className="mt-1 rounded-sm" placeholder="Brief description" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Base URL *</Label>
                <Input value={restForm.base_url} onChange={(e) => setRestForm({...restForm, base_url: e.target.value})} className="mt-1 rounded-sm" placeholder="https://api.example.com/v1" data-testid="rest-base-url" />
              </div>
              {/* Access Mode & Authoritative */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Access Mode</Label>
                  <Select value={restForm.access_mode} onValueChange={(v) => setRestForm({...restForm, access_mode: v})}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="rest-access-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read_only">Read Only (Aggregation)</SelectItem>
                      <SelectItem value="read_write">Read & Write (Aggregation + Provisioning)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-navy-400 mt-1">{restForm.access_mode === 'read_only' ? 'Import only — no write-back to the source.' : 'Import + provision (grant/revoke) to the source.'}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Identity Authority</Label>
                  <div className="mt-2 flex items-center gap-3">
                    <Switch checked={restForm.authoritative} onCheckedChange={(val) => setRestForm({...restForm, authoritative: val})} data-testid="rest-authoritative-toggle" />
                    <span className="text-sm text-navy-700">{restForm.authoritative ? 'Authoritative' : 'Non-Authoritative'}</span>
                  </div>
                  <p className="text-[10px] text-navy-400 mt-1">{restForm.authoritative ? 'Creates & manages identities. Removes identities not in source.' : 'Creates accounts correlated to existing identities by email.'}</p>
                </div>
              </div>
              {restForm.authoritative && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <p className="text-xs text-amber-700">
                    <strong>Authoritative source:</strong> During aggregation, this connector will create new identities for accounts not found in the system and disable identities that no longer appear in the source data.
                  </p>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Authentication Method</Label>
                <Select value={restForm.auth.method} onValueChange={(v) => setRestForm({...restForm, auth: {...restForm.auth, method: v}})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="rest-auth-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="api_key">API Key</SelectItem>
                    <SelectItem value="basic">Basic Auth (Username/Password)</SelectItem>
                    <SelectItem value="oauth2_client_credentials">OAuth2 Client Credentials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {restForm.auth.method === 'bearer' && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Bearer Token</Label>
                  <Input type="password" value={restForm.auth.bearer_token} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, bearer_token: e.target.value}})} className="mt-1 rounded-sm" placeholder="Enter token" data-testid="rest-bearer-token" />
                </div>
              )}
              {restForm.auth.method === 'api_key' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">API Key</Label>
                    <Input type="password" value={restForm.auth.api_key} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, api_key: e.target.value}})} className="mt-1 rounded-sm" placeholder="Enter API key" data-testid="rest-api-key" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider">Header Name</Label>
                      <Input value={restForm.auth.api_key_header} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, api_key_header: e.target.value}})} className="mt-1 rounded-sm" placeholder="Authorization" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider">Key Prefix</Label>
                      <Input value={restForm.auth.api_key_prefix} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, api_key_prefix: e.target.value}})} className="mt-1 rounded-sm" placeholder="Api-Key" />
                    </div>
                  </div>
                </div>
              )}
              {restForm.auth.method === 'basic' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">Username</Label>
                    <Input value={restForm.auth.username} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, username: e.target.value}})} className="mt-1 rounded-sm" placeholder="Username" data-testid="rest-basic-username" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">Password</Label>
                    <Input type="password" value={restForm.auth.password} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, password: e.target.value}})} className="mt-1 rounded-sm" placeholder="Password" data-testid="rest-basic-password" />
                  </div>
                </div>
              )}
              {restForm.auth.method === 'oauth2_client_credentials' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">Token URL</Label>
                    <Input value={restForm.auth.token_url} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, token_url: e.target.value}})} className="mt-1 rounded-sm" placeholder="https://auth.example.com/oauth/token" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider">Client ID</Label>
                      <Input value={restForm.auth.client_id} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, client_id: e.target.value}})} className="mt-1 rounded-sm" placeholder="Client ID" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider">Client Secret</Label>
                      <Input type="password" value={restForm.auth.client_secret} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, client_secret: e.target.value}})} className="mt-1 rounded-sm" placeholder="Client Secret" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">Scope (optional)</Label>
                    <Input value={restForm.auth.scope} onChange={(e) => setRestForm({...restForm, auth: {...restForm.auth, scope: e.target.value}})} className="mt-1 rounded-sm" placeholder="e.g., read:users" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Accounts Endpoint */}
          {restConnectorStep === 2 && (
            <div className="space-y-4 py-2">
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                <p className="text-xs text-blue-700">Configure the API endpoint that returns user accounts. Use JSONPath expressions (e.g., <code className="bg-blue-100 px-1 rounded">$.id</code>, <code className="bg-blue-100 px-1 rounded">$.user.email</code>) to map response fields.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Accounts URL *</Label>
                  <Input value={restForm.endpoints.accounts.url} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, accounts: {...restForm.endpoints.accounts, url: e.target.value}}})} className="mt-1 rounded-sm" placeholder="/users or full URL" data-testid="rest-accounts-url" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Response Array Path</Label>
                  <Input value={restForm.endpoints.accounts.response_path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, accounts: {...restForm.endpoints.accounts, response_path: e.target.value}}})} className="mt-1 rounded-sm" placeholder="$.data or $.results" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Pagination Type</Label>
                  <Select value={restForm.endpoints.accounts.pagination.type} onValueChange={(v) => setRestForm({...restForm, endpoints: {...restForm.endpoints, accounts: {...restForm.endpoints.accounts, pagination: {...restForm.endpoints.accounts.pagination, type: v}}}})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="offset">Offset</SelectItem>
                      <SelectItem value="page">Page Number</SelectItem>
                      <SelectItem value="cursor">Cursor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Page Size</Label>
                  <Input type="number" value={restForm.endpoints.accounts.pagination.page_size} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, accounts: {...restForm.endpoints.accounts, pagination: {...restForm.endpoints.accounts.pagination, page_size: parseInt(e.target.value) || 100}}}})} className="mt-1 rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Field Mapping (JSONPath)</Label>
                <div className="border rounded-sm p-3 space-y-2 bg-slate-50">
                  {Object.entries(restForm.endpoints.accounts.field_mapping).map(([field, path]) => (
                    <div key={field} className="grid grid-cols-5 gap-2 items-center">
                      <Label className="text-xs text-navy-600 col-span-2">{field} {(field === 'account_id') && <span className="text-red-500">*</span>}</Label>
                      <Input value={path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, accounts: {...restForm.endpoints.accounts, field_mapping: {...restForm.endpoints.accounts.field_mapping, [field]: e.target.value}}}})} className="col-span-3 rounded-sm h-8 text-xs" placeholder={`$.${field}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Entitlements & Assignments */}
          {restConnectorStep === 3 && (
            <div className="space-y-4 py-2">
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                <p className="text-xs text-blue-700">Configure endpoints for entitlements (groups/permissions) and their assignments to accounts. Leave blank if not applicable.</p>
              </div>
              <h4 className="font-semibold text-sm text-navy-900 border-b pb-1">Entitlements Endpoint</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Entitlements URL</Label>
                  <Input value={restForm.endpoints.entitlements.url} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, entitlements: {...restForm.endpoints.entitlements, url: e.target.value}}})} className="mt-1 rounded-sm" placeholder="/groups or /permissions" data-testid="rest-entitlements-url" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Response Array Path</Label>
                  <Input value={restForm.endpoints.entitlements.response_path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, entitlements: {...restForm.endpoints.entitlements, response_path: e.target.value}}})} className="mt-1 rounded-sm" placeholder="$.data" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Entitlement Field Mapping</Label>
                <div className="border rounded-sm p-3 space-y-2 bg-slate-50">
                  {Object.entries(restForm.endpoints.entitlements.field_mapping).map(([field, path]) => (
                    <div key={field} className="grid grid-cols-5 gap-2 items-center">
                      <Label className="text-xs text-navy-600 col-span-2">{field}</Label>
                      <Input value={path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, entitlements: {...restForm.endpoints.entitlements, field_mapping: {...restForm.endpoints.entitlements.field_mapping, [field]: e.target.value}}}})} className="col-span-3 rounded-sm h-8 text-xs" placeholder={`$.${field}`} />
                    </div>
                  ))}
                </div>
              </div>
              <h4 className="font-semibold text-sm text-navy-900 border-b pb-1 mt-4">Assignments Endpoint</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Assignments URL</Label>
                  <Input value={restForm.endpoints.assignments.url} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, assignments: {...restForm.endpoints.assignments, url: e.target.value}}})} className="mt-1 rounded-sm" placeholder="/user-group-memberships" data-testid="rest-assignments-url" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Response Array Path</Label>
                  <Input value={restForm.endpoints.assignments.response_path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, assignments: {...restForm.endpoints.assignments, response_path: e.target.value}}})} className="mt-1 rounded-sm" placeholder="$.data" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Assignment Field Mapping</Label>
                <div className="border rounded-sm p-3 space-y-2 bg-slate-50">
                  {Object.entries(restForm.endpoints.assignments.field_mapping).map(([field, path]) => (
                    <div key={field} className="grid grid-cols-5 gap-2 items-center">
                      <Label className="text-xs text-navy-600 col-span-2">{field}</Label>
                      <Input value={path} onChange={(e) => setRestForm({...restForm, endpoints: {...restForm.endpoints, assignments: {...restForm.endpoints.assignments, field_mapping: {...restForm.endpoints.assignments.field_mapping, [field]: e.target.value}}}})} className="col-span-3 rounded-sm h-8 text-xs" placeholder={`$.${field}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Provisioning */}
          {restConnectorStep === 4 && (
            <div className="space-y-4 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-xs text-amber-700">Optional: Configure write-back endpoints to grant or revoke access in the target application. Use <code className="bg-amber-100 px-1 rounded">{'{{account_id}}'}</code> and <code className="bg-amber-100 px-1 rounded">{'{{entitlement_id}}'}</code> as placeholders in URLs and body templates.</p>
              </div>
              <h4 className="font-semibold text-sm text-navy-900 border-b pb-1">Grant Access</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Grant URL</Label>
                  <Input value={restForm.provisioning.grant_url} onChange={(e) => setRestForm({...restForm, provisioning: {...restForm.provisioning, grant_url: e.target.value}})} className="mt-1 rounded-sm" placeholder="/users/{{account_id}}/groups/{{entitlement_id}}" data-testid="rest-grant-url" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Method</Label>
                  <Select value={restForm.provisioning.grant_method} onValueChange={(v) => setRestForm({...restForm, provisioning: {...restForm.provisioning, grant_method: v}})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Grant Body Template (JSON, optional)</Label>
                <textarea value={typeof restForm.provisioning.grant_body_template === 'object' ? JSON.stringify(restForm.provisioning.grant_body_template, null, 2) : restForm.provisioning.grant_body_template} onChange={(e) => setRestForm({...restForm, provisioning: {...restForm.provisioning, grant_body_template: e.target.value}})} className="mt-1 w-full rounded-sm border border-slate-200 p-2 text-xs font-mono h-20 resize-none" placeholder='{"user_id": "{{account_id}}", "group_id": "{{entitlement_id}}"}' />
              </div>
              <h4 className="font-semibold text-sm text-navy-900 border-b pb-1 mt-4">Revoke Access</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Revoke URL</Label>
                  <Input value={restForm.provisioning.revoke_url} onChange={(e) => setRestForm({...restForm, provisioning: {...restForm.provisioning, revoke_url: e.target.value}})} className="mt-1 rounded-sm" placeholder="/users/{{account_id}}/groups/{{entitlement_id}}" data-testid="rest-revoke-url" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Method</Label>
                  <Select value={restForm.provisioning.revoke_method} onValueChange={(v) => setRestForm({...restForm, provisioning: {...restForm.provisioning, revoke_method: v}})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Revoke Body Template (JSON, optional)</Label>
                <textarea value={typeof restForm.provisioning.revoke_body_template === 'object' ? JSON.stringify(restForm.provisioning.revoke_body_template, null, 2) : restForm.provisioning.revoke_body_template} onChange={(e) => setRestForm({...restForm, provisioning: {...restForm.provisioning, revoke_body_template: e.target.value}})} className="mt-1 w-full rounded-sm border border-slate-200 p-2 text-xs font-mono h-20 resize-none" placeholder='{"user_id": "{{account_id}}", "action": "remove"}' />
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div>
              {restConnectorStep > 1 && (
                <Button variant="outline" onClick={() => setRestConnectorStep(s => s - 1)} className="rounded-sm">Back</Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRestConnectorDialog(false)} className="rounded-sm">Cancel</Button>
              {restConnectorStep < 4 ? (
                <Button onClick={() => setRestConnectorStep(s => s + 1)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-sm" data-testid="rest-next-step">
                  Next
                </Button>
              ) : (
                <Button onClick={handleSaveRestConnector} disabled={savingConnector} className="bg-teal-600 hover:bg-teal-700 text-white rounded-sm" data-testid="rest-save-connector">
                  {savingConnector ? 'Saving...' : 'Save Connector'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Additional Azure AD Connector Dialog */}
      <Dialog open={showAzureConnectorDialog} onOpenChange={setShowAzureConnectorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                  <path d="M0 0h10v10H0z" fill="#F25022"/><path d="M11 0h10v10H11z" fill="#7FBA00"/>
                  <path d="M0 11h10v10H0z" fill="#00A4EF"/><path d="M11 11h10v10H11z" fill="#FFB900"/>
                </svg>
                {selectedSource?.azure_connector_id ? 'Edit Azure AD Connector' : 'New Azure AD Connector'}
              </div>
            </DialogTitle>
            <DialogDescription>Connect an Azure Active Directory tenant to import users and groups.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Connector Name *</Label>
              <Input value={azureForm.name} onChange={(e) => setAzureForm({...azureForm, name: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Production Azure AD, Dev Tenant" data-testid="azure-extra-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input value={azureForm.description} onChange={(e) => setAzureForm({...azureForm, description: e.target.value})} className="mt-1 rounded-sm" placeholder="Brief description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Tenant ID *</Label>
                <Input value={azureForm.tenant_id} onChange={(e) => setAzureForm({...azureForm, tenant_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="azure-extra-tenant-id" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Client ID *</Label>
                <Input value={azureForm.client_id} onChange={(e) => setAzureForm({...azureForm, client_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" data-testid="azure-extra-client-id" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Client Secret *</Label>
              <Input type="password" value={azureForm.client_secret} onChange={(e) => setAzureForm({...azureForm, client_secret: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder="Enter client secret" data-testid="azure-extra-client-secret" />
            </div>
            {/* Access Mode & Authoritative */}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Access Mode</Label>
                <Select value={azureForm.access_mode} onValueChange={(v) => setAzureForm({...azureForm, access_mode: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="azure-extra-access-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read_only">Read Only (Aggregation)</SelectItem>
                    <SelectItem value="read_write">Read & Write (Aggregation + Provisioning)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-navy-400 mt-1">{azureForm.access_mode === 'read_only' ? 'Import only — no write-back to Azure AD.' : 'Import + provision to Azure AD.'}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Identity Authority</Label>
                <div className="mt-2 flex items-center gap-3">
                  <Switch checked={azureForm.authoritative} onCheckedChange={(val) => setAzureForm({...azureForm, authoritative: val})} data-testid="azure-extra-authoritative-toggle" />
                  <span className="text-sm text-navy-700">{azureForm.authoritative ? 'Authoritative' : 'Non-Authoritative'}</span>
                </div>
                <p className="text-[10px] text-navy-400 mt-1">{azureForm.authoritative ? 'Creates & manages identities from this tenant.' : 'Correlates accounts to existing identities by email.'}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAzureConnectorDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveAzureConnector} disabled={savingAzureConnector} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm" data-testid="azure-extra-save">
              {savingAzureConnector ? 'Saving...' : (selectedSource?.azure_connector_id ? 'Update Connector' : 'Create Connector')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProvisioningTab() {
  const [status, setStatus] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [userForm, setUserForm] = useState({ identity_id: '', action: 'create' });
  const [groupForm, setGroupForm] = useState({ identity_id: '', group_id: '', action: 'add' });
  const [provisioning, setProvisioning] = useState(false);
  const [recentActions, setRecentActions] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, identitiesRes, entitlementsRes, auditRes] = await Promise.all([
        API.get('/admin/connector/provision/status'),
        API.get('/identities', { params: { limit: 200, sort_by: 'display_name', sort_order: 'asc' } }),
        API.get('/entitlements', { params: { limit: 200 } }),
        API.get('/admin/audit', { params: { entity_type: 'connector', limit: 10 } })
      ]);
      setStatus(statusRes.data);
      setIdentities(identitiesRes.data.items);
      setEntitlements(entitlementsRes.data.items);
      setRecentActions(auditRes.data.items.filter(a => a.action.includes('provision')));
    } catch (err) { void 0; }
  };

  const handleUserProvision = async () => {
    if (!userForm.identity_id) { toast.error('Select an identity'); return; }
    setProvisioning(true);
    try {
      const res = await API.post('/admin/connector/provision/user', userForm);
      toast.success(res.data.message || 'User provisioning completed');
      setShowUserDialog(false);
      setUserForm({ identity_id: '', action: 'create' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Provisioning failed'); }
    finally { setProvisioning(false); }
  };

  const handleGroupProvision = async () => {
    if (!groupForm.identity_id || !groupForm.group_id) { toast.error('Select identity and group'); return; }
    setProvisioning(true);
    try {
      const res = await API.post('/admin/connector/provision/group', groupForm);
      toast.success(res.data.message || 'Group membership updated');
      setShowGroupDialog(false);
      setGroupForm({ identity_id: '', group_id: '', action: 'add' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Provisioning failed'); }
    finally { setProvisioning(false); }
  };

  const actionColor = (action) => {
    if (action.includes('create') || action.includes('add')) return 'bg-emerald-100 text-emerald-700';
    if (action.includes('update')) return 'bg-blue-100 text-blue-700';
    if (action.includes('disable') || action.includes('remove')) return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-600';
  };

  return (
    <div className="mt-4 space-y-6">
      {/* Status Card */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center">
              <Cloud size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Azure AD Provisioning</h3>
              <p className="text-xs text-navy-500">Write-back changes to Azure AD (create users, manage group memberships)</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${status?.connector_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {status?.connector_configured ? 'Ready' : 'Not Configured'}
          </span>
        </div>

        {!status?.connector_configured ? (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-700">
            <p className="font-medium">Connector not configured</p>
            <p className="text-xs mt-1">Configure the Azure AD Data Connector in the "Connector" tab first to enable provisioning.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-navy-700">
                <div className={`w-2 h-2 rounded-full ${status?.can_create_users ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span>Create Users: {status?.can_create_users ? 'Available' : 'Requires Directory.ReadWrite.All'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-navy-700">
                <div className={`w-2 h-2 rounded-full ${status?.can_update_users ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span>Update Users: {status?.can_update_users ? 'Available' : 'Requires User.ReadWrite.All'}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-navy-700">
                <div className={`w-2 h-2 rounded-full ${status?.can_manage_groups ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span>Manage Groups: {status?.can_manage_groups ? 'Available' : 'Requires GroupMember.ReadWrite.All'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-navy-700">
                <div className={`w-2 h-2 rounded-full ${status?.can_disable_users ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span>Disable Users: {status?.can_disable_users ? 'Available' : 'Requires User.ReadWrite.All'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {status?.connector_configured && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-sm bg-blue-100 flex items-center justify-center">
                <UserPlus size={16} className="text-blue-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-navy-900">User Provisioning</h4>
                <p className="text-xs text-navy-500">Create, update, or disable users in Azure AD</p>
              </div>
            </div>
            <Button onClick={() => setShowUserDialog(true)} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm" data-testid="open-user-provision-dialog">
              <UserPlus size={14} className="mr-1.5" /> Provision User
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-sm bg-purple-100 flex items-center justify-center">
                <Users size={16} className="text-purple-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-navy-900">Group Membership</h4>
                <p className="text-xs text-navy-500">Add or remove users from Azure AD groups</p>
              </div>
            </div>
            <Button onClick={() => setShowGroupDialog(true)} className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-sm" data-testid="open-group-provision-dialog">
              <Users size={14} className="mr-1.5" /> Manage Group Membership
            </Button>
          </div>
        </div>
      )}

      {/* Recent Provisioning Actions */}
      {recentActions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h3 className="text-sm font-bold text-navy-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Recent Provisioning Actions</h3>
          <div className="space-y-2">
            {recentActions.map(action => (
              <div key={action.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-sm">
                <div className="flex items-center gap-3">
                  <Badge className={`text-[10px] uppercase ${actionColor(action.action)}`}>{action.action}</Badge>
                  <span className="text-sm text-navy-700">{action.entity_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-navy-500">{action.user_name}</span>
                  <span className="text-xs text-navy-400">{new Date(action.timestamp).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-sm bg-amber-100 flex items-center justify-center shrink-0">
            <Cloud size={16} className="text-amber-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-amber-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Required Azure AD Permissions</h4>
            <p className="text-xs text-amber-700 mt-1">
              For full provisioning capabilities, ensure your Azure AD App Registration has the following <strong>Application</strong> permissions:
            </p>
            <ul className="text-xs text-amber-700 mt-2 list-disc list-inside space-y-1">
              <li><code className="bg-amber-100 px-1 rounded">User.ReadWrite.All</code> - Create and update users</li>
              <li><code className="bg-amber-100 px-1 rounded">GroupMember.ReadWrite.All</code> - Add/remove group members</li>
              <li><code className="bg-amber-100 px-1 rounded">Directory.ReadWrite.All</code> - Full directory access</li>
            </ul>
          </div>
        </div>
      </div>

      {/* User Provisioning Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Provision User to Azure AD</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Identity</Label>
              <Select value={userForm.identity_id} onValueChange={(v) => setUserForm({...userForm, identity_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-provision-identity-select">
                  <SelectValue placeholder="Select an identity" />
                </SelectTrigger>
                <SelectContent>
                  {identities.map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      <div className="flex items-center gap-2">
                        <span>{i.display_name}</span>
                        {i.azure_id && <Badge className="bg-blue-100 text-blue-700 text-[8px]">Has Azure ID</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Action</Label>
              <Select value={userForm.action} onValueChange={(v) => setUserForm({...userForm, action: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-provision-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="create">
                    <div className="flex items-center gap-2">
                      <UserPlus size={14} className="text-emerald-600" />
                      <span>Create User in Azure AD</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="update">
                    <div className="flex items-center gap-2">
                      <UserCheck size={14} className="text-blue-600" />
                      <span>Update User Attributes</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="disable">
                    <div className="flex items-center gap-2">
                      <UserMinus size={14} className="text-red-600" />
                      <span>Disable User Account</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleUserProvision} disabled={provisioning} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm" data-testid="submit-user-provision">
              {provisioning ? 'Provisioning...' : 'Provision User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Membership Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Manage Group Membership</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Identity</Label>
              <Select value={groupForm.identity_id} onValueChange={(v) => setGroupForm({...groupForm, identity_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="group-provision-identity-select">
                  <SelectValue placeholder="Select an identity" />
                </SelectTrigger>
                <SelectContent>
                  {identities.filter(i => i.azure_id).map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-navy-400 mt-1">Only identities with Azure AD IDs are shown</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Group (Entitlement)</Label>
              <Select value={groupForm.group_id} onValueChange={(v) => setGroupForm({...groupForm, group_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="group-provision-group-select">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {entitlements.filter(e => e.azure_id).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-navy-400 mt-1">Only Azure AD groups are shown</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Action</Label>
              <Select value={groupForm.action} onValueChange={(v) => setGroupForm({...groupForm, action: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="group-provision-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex items-center gap-2">
                      <Plus size={14} className="text-emerald-600" />
                      <span>Add to Group</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="remove">
                    <div className="flex items-center gap-2">
                      <Trash size={14} className="text-red-600" />
                      <span>Remove from Group</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleGroupProvision} disabled={provisioning} className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm" data-testid="submit-group-provision">
              {provisioning ? 'Processing...' : 'Update Membership'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SsoTab() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ tenant_id: '', client_id: '', client_secret: '', redirect_uri: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    API.get('/admin/sso-config').then(r => {
      setConfig(r.data);
      setForm({
        tenant_id: r.data.tenant_id || '',
        client_id: r.data.client_id || '',
        client_secret: '',
        redirect_uri: r.data.redirect_uri || '',
        enabled: r.data.enabled ?? false
      });
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.post('/admin/sso-config', form);
      setConfig(res.data);
      setForm(f => ({ ...f, client_secret: '' }));
      toast.success('SSO configuration saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await API.post('/admin/sso-config/test');
      if (res.data.status === 'connected') toast.success(res.data.message);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const statusColor = (s) => {
    if (s === 'connected') return 'bg-emerald-100 text-emerald-700';
    if (s === 'configured') return 'bg-blue-100 text-blue-700';
    if (s === 'error') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-500';
  };

  // Get the current app URL for redirect URI suggestion
  const suggestedRedirectUri = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '';

  return (
    <div className="mt-4 space-y-6">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center">
              <Key size={20} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Azure AD Single Sign-On (SSO)</h3>
              <p className="text-xs text-navy-500">Allow users to log in with their Azure AD accounts</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${statusColor(config?.status)}`}>
            {config?.status || 'not configured'}
          </span>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} data-testid="sso-enabled-toggle" />
            <Label className="text-sm font-medium">Enable SSO Login</Label>
            {form.enabled && (
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Tenant ID</Label>
              <Input value={form.tenant_id} onChange={(e) => setForm({...form, tenant_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="sso-tenant-id" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Client ID (Application ID)</Label>
              <Input value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" data-testid="sso-client-id" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Client Secret</Label>
              <Input type="password" value={form.client_secret} onChange={(e) => setForm({...form, client_secret: e.target.value})} className="mt-1 rounded-sm font-mono text-sm" placeholder={config?.client_secret_masked || 'Enter secret'} data-testid="sso-client-secret" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Redirect URI</Label>
              <Input value={form.redirect_uri} onChange={(e) => setForm({...form, redirect_uri: e.target.value})} className="mt-1 rounded-sm text-sm" placeholder={suggestedRedirectUri} data-testid="sso-redirect-uri" />
              <p className="text-[10px] text-navy-400 mt-1">Suggested: {suggestedRedirectUri}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-sso-button">
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button onClick={handleTest} disabled={testing} variant="outline" className="rounded-sm" data-testid="test-sso-button">
              <Lightning size={14} className="mr-1.5" /> {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>
        </div>
      </div>

      {/* Azure App Registration Setup Guide */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <h3 className="text-sm font-bold text-navy-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Azure App Registration Setup</h3>
        <div className="space-y-3 text-sm text-navy-600">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">1</span>
            </div>
            <p>Go to <a href="https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Azure Portal → Azure AD → App registrations</a> and create a new registration.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">2</span>
            </div>
            <div>
              <p>Set the <strong>Redirect URI</strong> as a "Web" platform with:</p>
              <code className="block mt-1 px-2 py-1 bg-slate-100 rounded text-xs font-mono">{suggestedRedirectUri}</code>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">3</span>
            </div>
            <p>Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong> from the app's Overview page.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">4</span>
            </div>
            <p>Go to <strong>Certificates & secrets</strong>, create a new client secret, and copy its value immediately.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">5</span>
            </div>
            <p>Under <strong>API permissions</strong>, ensure <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">User.Read</code> (delegated) is added and admin consent is granted.</p>
          </div>
        </div>
      </div>

      {/* SSO Flow Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-sm bg-slate-200 flex items-center justify-center shrink-0">
            <Key size={16} className="text-navy-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>How SSO Works</h4>
            <p className="text-xs text-navy-600 mt-1">
              When SSO is enabled, users will see a <strong>"Sign in with Azure AD"</strong> button on the login page. 
              Clicking it redirects them to Microsoft's login page, and upon successful authentication, they're redirected back with an authorization code that's exchanged for user information.
            </p>
            <p className="text-xs text-navy-500 mt-2">
              New Azure AD users are automatically created in the IAG platform with "viewer" role. Admins can then assign appropriate roles.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  const loadLogs = useCallback(async () => {
    try {
      const res = await API.get('/admin/audit', { params: { search: search || undefined, entity_type: (entityFilter && entityFilter !== 'all') ? entityFilter : undefined, limit: 100 } });
      setLogs(res.data.items);
      setTotal(res.data.total);
    } catch (err) { void 0; }
  }, [search, entityFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const actionColor = (action) => {
    if (action === 'create') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (action === 'update' || action === 'configure') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (action === 'delete' || action === 'revoke') return 'bg-red-100 text-red-700 border-red-200';
    if (action.includes('provision')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (action.includes('login') || action.includes('sso')) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-navy-600 border-slate-200';
  };

  // Human-readable field name mapping
  const fieldLabels = {
    display_name: 'Display Name',
    job_title: 'Job Title',
    lifecycle_state: 'Lifecycle State',
    manager_id: 'Manager ID',
    manager_name: 'Manager',
    azure_id: 'Azure ID',
    tenant_id: 'Tenant ID',
    client_id: 'Client ID',
    client_secret: 'Client Secret',
    redirect_uri: 'Redirect URI',
    assignment_type: 'Assignment Type',
    expires_at: 'Expiration Date',
    entitlements: 'Entitlements',
    roles: 'Roles',
    owner_id: 'Owner ID',
    owner_name: 'Owner',
    connector_type: 'Connector Type',
    correlation_status: 'Correlation Status',
    requestable: 'Requestable',
    enforcement: 'Enforcement',
    severity: 'Severity',
  };

  const formatFieldName = (key) => {
    return fieldLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Format a single value for display
  const formatValue = (val, fieldKey = '') => {
    // Handle null/undefined
    if (val === null || val === undefined) {
      return <span className="text-navy-400 italic">empty</span>;
    }
    
    // Handle boolean
    if (typeof val === 'boolean') {
      return (
        <span className={val ? 'text-emerald-600' : 'text-red-600'}>
          {val ? 'Yes' : 'No'}
        </span>
      );
    }
    
    // Handle arrays (like entitlements, roles)
    if (Array.isArray(val)) {
      if (val.length === 0) {
        return <span className="text-navy-400 italic">none</span>;
      }
      // If array of simple strings/ids
      if (val.every(item => typeof item === 'string')) {
        if (val.length <= 3) {
          return val.join(', ');
        }
        return `${val.slice(0, 2).join(', ')} +${val.length - 2} more`;
      }
      // If array of objects (complex)
      return `${val.length} item${val.length !== 1 ? 's' : ''}`;
    }
    
    // Handle objects
    if (typeof val === 'object') {
      // Check if it looks like a date object
      if (val.$date) {
        return new Date(val.$date).toLocaleDateString();
      }
      // Format as compact readable summary
      const keys = Object.keys(val);
      if (keys.length === 0) {
        return <span className="text-navy-400 italic">empty</span>;
      }
      if (keys.length <= 2) {
        return keys.map(k => `${k}: ${val[k]}`).join(', ');
      }
      return `${keys.length} properties`;
    }
    
    // Handle date strings (ISO format)
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
      return new Date(val).toLocaleString();
    }
    
    // Handle masked secrets
    if (typeof val === 'string' && fieldKey && 
        (fieldKey.includes('secret') || fieldKey.includes('password') || fieldKey.includes('key'))) {
      if (val.includes('*')) return val; // Already masked
      return '••••••••';
    }
    
    return String(val);
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <Input placeholder="Search audit logs..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm h-9 rounded-sm" data-testid="audit-search-input" />
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-48 h-9 rounded-sm" data-testid="audit-entity-filter">
            <SelectValue placeholder="All entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="identity">Identity</SelectItem>
            <SelectItem value="role">Role</SelectItem>
            <SelectItem value="entitlement">Entitlement</SelectItem>
            <SelectItem value="application">Application</SelectItem>
            <SelectItem value="access_request">Access Request</SelectItem>
            <SelectItem value="workflow">Workflow</SelectItem>
            <SelectItem value="connector">Connector</SelectItem>
            <SelectItem value="admin_role">Admin Role</SelectItem>
            <SelectItem value="sso">SSO</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-navy-500">{total} records</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 w-44">Timestamp</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">User</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Action</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Entity</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Target</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Changes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-sm text-navy-400">No audit logs found.</TableCell></TableRow>
            ) : logs.map(log => (
              <TableRow 
                key={log.id} 
                className={`hover:bg-slate-50 transition-colors text-sm cursor-pointer ${expandedLog === log.id ? 'bg-slate-50' : ''}`} 
                data-testid={`audit-row-${log.id}`}
                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
              >
                <TableCell className="text-xs text-navy-500 font-mono whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</TableCell>
                <TableCell className="text-navy-700 font-medium">{log.user_name}</TableCell>
                <TableCell>
                  <Badge className={`text-[10px] uppercase border ${actionColor(log.action)}`}>{log.action}</Badge>
                </TableCell>
                <TableCell>
                  <span className="text-[10px] px-2 py-0.5 rounded-sm bg-slate-100 text-navy-600 font-bold uppercase">{log.entity_type}</span>
                </TableCell>
                <TableCell className="text-navy-700 max-w-xs truncate" title={log.entity_name}>{log.entity_name}</TableCell>
                <TableCell className="text-xs text-navy-500">
                  {log.changes && Object.keys(log.changes).length > 0 ? (
                    expandedLog === log.id ? (
                      <div className="bg-slate-50 rounded p-3 space-y-2 border border-slate-200">
                        <p className="text-[10px] font-bold uppercase text-navy-500 mb-2">Change Details</p>
                        {Object.entries(log.changes).map(([key, val]) => (
                          <div key={key} className="flex items-start gap-2 pb-2 border-b border-slate-100 last:border-0 last:pb-0">
                            <span className="font-bold text-navy-700 min-w-[100px] shrink-0 text-xs">{formatFieldName(key)}:</span>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="px-2 py-0.5 bg-red-50 border border-red-200 rounded text-red-600 text-xs">
                                {formatValue(val.from, key)}
                              </span>
                              <span className="text-navy-400">→</span>
                              <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-emerald-600 text-xs">
                                {formatValue(val.to, key)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-blue-600 text-[10px] hover:underline">
                        {Object.keys(log.changes).length} field(s) changed • Click to expand
                      </span>
                    )
                  ) : (
                    <span className="text-navy-400">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px]">
        <span className="text-navy-500">Action Types:</span>
        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">create</Badge>
        <Badge className="bg-blue-100 text-blue-700 border border-blue-200">update</Badge>
        <Badge className="bg-red-100 text-red-700 border border-red-200">delete</Badge>
        <Badge className="bg-purple-100 text-purple-700 border border-purple-200">provision</Badge>
        <Badge className="bg-amber-100 text-amber-700 border border-amber-200">login</Badge>
      </div>
    </div>
  );
}

function AdminRolesTab() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [form, setForm] = useState({ user_email: '', user_name: '', roles: [] });
  const [editingUser, setEditingUser] = useState(null);
  const [editRoles, setEditRoles] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await API.get('/admin/roles');
      setRoles(res.data.items);
      setUsers(res.data.users);
      setAvailableRoles(res.data.available_roles || []);
    } catch (err) { void 0; }
  };

  const handleCreate = async () => {
    try {
      await API.post('/admin/roles', { 
        user_email: form.user_email, 
        user_name: form.user_name, 
        roles: form.roles 
      });
      toast.success('Admin roles assigned');
      setShowDialog(false);
      setForm({ user_email: '', user_name: '', roles: [] });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Get roles from admin_roles array or fallback to single admin_role
    const userRoles = user.admin_roles || (user.admin_role ? [user.admin_role] : []);
    setEditRoles(userRoles);
    setShowEditDialog(true);
  };

  const handleUpdateRole = async () => {
    if (!editingUser) return;
    try {
      await API.put(`/admin/roles/user/${editingUser.id}`, { roles: editRoles });
      toast.success(`Roles updated successfully`);
      setShowEditDialog(false);
      setEditingUser(null);
      load();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to update roles'); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove all roles from this user?')) return;
    try { await API.delete(`/admin/roles/${id}`); toast.success('Removed'); load(); }
    catch (err) { toast.error('Failed to remove'); }
  };

  const toggleRole = (role, setFn, currentRoles) => {
    // Admin role cannot be combined with other roles
    if (role === 'admin') {
      setFn(['admin']);
      return;
    }
    
    // If switching from admin to other roles, clear admin
    let newRoles = currentRoles.filter(r => r !== 'admin');
    
    if (newRoles.includes(role)) {
      newRoles = newRoles.filter(r => r !== role);
    } else {
      newRoles = [...newRoles, role];
    }
    setFn(newRoles);
  };

  const roleColor = (r) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      certification_manager: 'bg-purple-100 text-purple-700',
      sod_manager: 'bg-amber-100 text-amber-700',
      application_manager: 'bg-teal-100 text-teal-700',
      provisioning_manager: 'bg-indigo-100 text-indigo-700',
      role_manager: 'bg-blue-100 text-blue-700',
      request_approver: 'bg-green-100 text-green-700',
      viewer: 'bg-slate-100 text-navy-600'
    };
    return colors[r] || 'bg-slate-100 text-navy-600';
  };

  const getUserRoles = (user) => {
    return user.admin_roles || (user.admin_role ? [user.admin_role] : []);
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-navy-500">{users.length} users with admin roles</p>
          <p className="text-xs text-navy-400 mt-1">Users can have multiple roles unless they are Admin (full access)</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="add-admin-role-button">
          <Plus size={16} className="mr-1.5" /> Add Admin Role
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">User</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Email</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Roles</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-sm text-navy-400">
                  No users found. Add a user role to get started.
                </TableCell>
              </TableRow>
            ) : users.map(u => (
              <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-role-row-${u.id}`}>
                <TableCell className="font-medium text-sm text-navy-900">{u.name}</TableCell>
                <TableCell className="text-sm text-navy-600">{u.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {getUserRoles(u).map(role => (
                      <span key={role} className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${roleColor(role)}`}>
                        {role.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.email !== 'admin@iag.local' && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(u)}
                          className="text-navy-600 hover:text-navy-900"
                          data-testid={`edit-role-${u.id}`}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(u.id)} 
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-role-${u.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                    {u.email === 'admin@iag.local' && (
                      <span className="text-xs text-navy-400 italic">Default admin</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add New Role Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Assign Admin Roles</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">User Name</Label>
              <Input value={form.user_name} onChange={(e) => setForm({...form, user_name: e.target.value})} className="mt-1 rounded-sm" data-testid="admin-role-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">User Email</Label>
              <Input value={form.user_email} onChange={(e) => setForm({...form, user_email: e.target.value})} className="mt-1 rounded-sm" data-testid="admin-role-email-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Roles</Label>
              <p className="text-xs text-navy-400 mt-1 mb-2">Select one or more roles. Admin role grants full access and cannot be combined.</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {availableRoles.map(role => (
                  <div 
                    key={role.key}
                    onClick={() => toggleRole(role.key, (r) => setForm({...form, roles: r}), form.roles)}
                    className={`border rounded-sm p-3 cursor-pointer transition-all ${
                      form.roles.includes(role.key) 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${form.roles.includes('admin') && role.key !== 'admin' ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                        form.roles.includes(role.key) ? 'bg-orange-500 border-orange-500' : 'border-slate-300'
                      }`}>
                        {form.roles.includes(role.key) && <Check size={12} className="text-white" />}
                      </div>
                      <span className={`text-sm font-medium ${form.roles.includes(role.key) ? 'text-orange-700' : 'text-navy-700'}`}>
                        {role.name}
                      </span>
                    </div>
                    <p className="text-xs text-navy-500 mt-1 ml-6">{role.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={handleCreate} 
              disabled={!form.user_email || !form.user_name || form.roles.length === 0}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" 
              data-testid="save-admin-role-button"
            >
              Assign Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Edit User Roles</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-sm p-4">
                <p className="text-sm font-medium text-navy-900">{editingUser.name}</p>
                <p className="text-xs text-navy-500">{editingUser.email}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {getUserRoles(editingUser).map(role => (
                    <span key={role} className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${roleColor(role)}`}>
                      {role.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Select Roles</Label>
                <p className="text-xs text-navy-400 mt-1 mb-2">Admin role grants full access and cannot be combined with other roles.</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {availableRoles.map(role => (
                    <div 
                      key={role.key}
                      onClick={() => toggleRole(role.key, setEditRoles, editRoles)}
                      className={`border rounded-sm p-3 cursor-pointer transition-all ${
                        editRoles.includes(role.key) 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      } ${editRoles.includes('admin') && role.key !== 'admin' ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                          editRoles.includes(role.key) ? 'bg-orange-500 border-orange-500' : 'border-slate-300'
                        }`}>
                          {editRoles.includes(role.key) && <Check size={12} className="text-white" />}
                        </div>
                        <span className={`text-sm font-medium ${editRoles.includes(role.key) ? 'text-orange-700' : 'text-navy-700'}`}>
                          {role.name}
                        </span>
                      </div>
                      <p className="text-xs text-navy-500 mt-1 ml-6">{role.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={handleUpdateRole} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" 
              data-testid="update-role-button"
              disabled={editRoles.length === 0}
            >
              Update Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EmailTab() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    host: '', port: 587, username: '', password: '', from_email: '', from_name: 'IAG Platform', use_tls: true, enabled: true
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    API.get('/admin/smtp').then(r => {
      setConfig(r.data);
      setForm({
        host: r.data.host || '',
        port: r.data.port || 587,
        username: r.data.username || '',
        password: '',
        from_email: r.data.from_email || '',
        from_name: r.data.from_name || 'IAG Platform',
        use_tls: r.data.use_tls ?? true,
        enabled: r.data.enabled ?? false
      });
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.post('/admin/smtp', form);
      setConfig(res.data);
      setForm(f => ({ ...f, password: '' }));
      toast.success('SMTP configuration saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    setTesting(true);
    try {
      const res = await API.post('/admin/smtp/test', { test_email: testEmail });
      if (res.data.status === 'success') toast.success(res.data.message);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const statusColor = (s) => {
    if (s === 'connected') return 'bg-emerald-100 text-emerald-700';
    if (s === 'configured') return 'bg-blue-100 text-blue-700';
    if (s === 'error') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-500';
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center">
              <Envelope size={20} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>SMTP Email Configuration</h3>
              <p className="text-xs text-navy-500">Configure outgoing email for notifications</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${statusColor(config?.status)}`}>
            {config?.status || 'not configured'}
          </span>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} />
            <Label className="text-sm font-medium">Enable Email Notifications</Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">SMTP Host</Label>
              <Input value={form.host} onChange={(e) => setForm({...form, host: e.target.value})} className="mt-1 rounded-sm text-sm" placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Port</Label>
              <Input type="number" value={form.port} onChange={(e) => setForm({...form, port: parseInt(e.target.value) || 587})} className="mt-1 rounded-sm text-sm" data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Username</Label>
              <Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} className="mt-1 rounded-sm text-sm" data-testid="smtp-username" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1 rounded-sm text-sm" placeholder={config?.password_masked || 'Enter password'} data-testid="smtp-password" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">From Email</Label>
              <Input value={form.from_email} onChange={(e) => setForm({...form, from_email: e.target.value})} className="mt-1 rounded-sm text-sm" placeholder="noreply@company.com" data-testid="smtp-from-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">From Name</Label>
              <Input value={form.from_name} onChange={(e) => setForm({...form, from_name: e.target.value})} className="mt-1 rounded-sm text-sm" data-testid="smtp-from-name" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({...form, use_tls: v})} />
            <Label className="text-sm">Use TLS (STARTTLS)</Label>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-smtp-button">
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <h3 className="text-sm font-bold text-navy-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Send Test Email</h3>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs font-semibold uppercase tracking-wider">Recipient Email</Label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="mt-1 rounded-sm text-sm" placeholder="test@example.com" data-testid="smtp-test-email" />
          </div>
          <Button onClick={handleTest} disabled={testing} variant="outline" className="rounded-sm h-10" data-testid="send-test-email-button">
            <Envelope size={14} className="mr-1.5" /> {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </div>
      </div>

      {/* Notification Events */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <h3 className="text-sm font-bold text-navy-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Notification Events</h3>
        <p className="text-xs text-navy-500 mb-4">Emails are automatically sent for the following events when SMTP is configured and enabled:</p>
        <div className="space-y-2">
          {[
            { event: 'Access Request Submitted', target: 'Admins & Approvers', color: 'bg-blue-100 text-blue-700' },
            { event: 'Access Request Approved/Rejected', target: 'Requester (Identity)', color: 'bg-emerald-100 text-emerald-700' },
            { event: 'Role Expiring Soon (7 days)', target: 'Identity + Manager', color: 'bg-amber-100 text-amber-700' },
            { event: 'Role Expired', target: 'Identity + Manager', color: 'bg-red-100 text-red-700' },
            { event: 'Renewal Sent to Manager', target: 'Manager + Admins', color: 'bg-purple-100 text-purple-700' },
            { event: 'Renewal Approved', target: 'Identity', color: 'bg-emerald-100 text-emerald-700' },
          ].map(item => (
            <div key={item.event} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${item.color}`}>{item.event}</span>
              </div>
              <span className="text-xs text-navy-500">{item.target}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CronTab() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const res = await API.get('/admin/cron/status');
      setStatus(res.data);
      setSelectedInterval(res.data.interval_seconds);
    } catch (err) { void 0; }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await API.post('/admin/cron/run-now');
      toast.success(res.data.message);
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to run'); }
    finally { setRunning(false); }
  };

  const handleSaveInterval = async () => {
    if (!selectedInterval || selectedInterval === status?.interval_seconds) return;
    setSaving(true);
    try {
      const res = await API.put('/admin/cron/interval', { interval_seconds: selectedInterval });
      toast.success(res.data.message || 'Interval updated');
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update interval'); }
    finally { setSaving(false); }
  };

  const formatInterval = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
    if (seconds < 86400) {
      const hours = Math.round(seconds / 3600);
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    return '24 hours';
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
              <Clock size={20} className="text-navy-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Expiry Check Scheduler</h3>
              <p className="text-xs text-navy-500">Automatic background task that checks for expired role assignments</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${status?.running ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {status?.running ? 'Running' : 'Stopped'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-navy-500 mb-1">Current Interval</p>
              <p className="text-sm font-medium text-navy-900">
                {status?.interval_seconds ? `Every ${formatInterval(status.interval_seconds)}` : '-'}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-navy-500 mb-1">Last Run</p>
              <p className="text-sm font-medium text-navy-900">{status?.last_run ? new Date(status.last_run).toLocaleString() : 'Never'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-navy-500 mb-1">Last Result</p>
              {status?.last_result ? (
                <div className="text-sm space-y-1">
                  <p className="text-navy-700">Renewals sent: <strong>{status.last_result.renewal_reminders_sent || 0}</strong></p>
                  <p className="text-navy-700">Expired processed: <strong>{status.last_result.expired_processed}</strong></p>
                  <p className="text-navy-700">Dynamic added: <strong>{status.last_result.dynamic_added || 0}</strong></p>
                  <p className="text-navy-700">Dynamic removed: <strong>{status.last_result.dynamic_removed || 0}</strong></p>
                  {(status.last_result.errors || []).length > 0 && (
                    <p className="text-red-500 text-xs">{status.last_result.errors.length} error(s)</p>
                  )}
                </div>
              ) : <p className="text-sm text-navy-400">No results yet</p>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 mt-4 border-t border-slate-100">
          <Button onClick={handleRunNow} disabled={running} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="run-cron-now-button">
            <ArrowsClockwise size={14} className={`mr-1.5 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running...' : 'Run Expiry Check Now'}
          </Button>
        </div>
      </div>

      {/* Interval Configuration */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-sm bg-blue-100 flex items-center justify-center">
            <GearSix size={16} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Scheduler Interval</h3>
            <p className="text-xs text-navy-500">Configure how often the background task runs</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Select Interval</Label>
            <Select value={selectedInterval?.toString()} onValueChange={(v) => setSelectedInterval(parseInt(v))}>
              <SelectTrigger className="mt-2 rounded-sm w-64" data-testid="cron-interval-select">
                <SelectValue placeholder="Select interval" />
              </SelectTrigger>
              <SelectContent>
                {(status?.interval_options || []).map(opt => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-navy-400 mt-1">
              The scheduler will check for expiring roles at this interval
            </p>
          </div>

          <Button 
            onClick={handleSaveInterval} 
            disabled={saving || !selectedInterval || selectedInterval === status?.interval_seconds}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm" 
            data-testid="save-cron-interval-button"
          >
            {saving ? 'Saving...' : 'Save Interval'}
          </Button>

          {selectedInterval && selectedInterval !== status?.interval_seconds && (
            <p className="text-xs text-amber-600">
              Unsaved change: {formatInterval(status?.interval_seconds || 3600)} → {formatInterval(selectedInterval)}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <h3 className="text-sm font-bold text-navy-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>How It Works</h3>
        <div className="space-y-3 text-sm text-navy-600">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">1</span>
            </div>
            <p>The scheduler runs automatically at the configured interval and checks all active role assignments for expiry.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">2</span>
            </div>
            <p><strong>Renewal Reminder:</strong> Assignments approaching expiry (based on role's renewal_reminder_days setting) trigger a renewal workflow for the user to confirm.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">3</span>
            </div>
            <p><strong>Expired:</strong> Fully expired assignments (with no pending renewal) are marked as expired and access is removed.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold text-orange-600">4</span>
            </div>
            <p><strong>Dynamic Roles:</strong> The scheduler also evaluates dynamic role rules and auto-assigns/removes roles based on identity attributes.</p>
          </div>
        </div>
      </div>
    </div>
  );
}


function FeaturesTab() {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      const res = await API.get('/features');
      setFeatures(res.data.features || {});
    } catch (err) {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (featureId, currentEnabled) => {
    setToggling(featureId);
    try {
      await API.put('/features', { feature_id: featureId, enabled: !currentEnabled });
      setFeatures(prev => ({
        ...prev,
        [featureId]: { ...prev[featureId], enabled: !currentEnabled }
      }));
      toast.success(`${features[featureId]?.label || featureId} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update feature');
    } finally {
      setToggling(null);
    }
  };

  const featureIcons = {
    certifications: ClipboardText,
    lifecycle: ArrowsClockwise,
    sod: Warning,
    role_mining: Brain,
    reports: ChartBar,
    local_login: Key,
  };

  if (loading) return <div className="p-8 text-center text-navy-400 text-sm">Loading features...</div>;

  return (
    <div className="space-y-6" data-testid="features-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-navy-900">Feature Management</h2>
          <p className="text-xs text-navy-500 mt-1">Enable or disable platform features. Disabled features are hidden from navigation and inaccessible.</p>
        </div>
      </div>

      <div className="grid gap-4">
        {Object.entries(features).map(([id, feature]) => {
          const Icon = featureIcons[id] || Lightning;
          const isEnabled = feature.enabled !== false;
          return (
            <div
              key={id}
              className={`bg-white border rounded-sm p-5 transition-all ${isEnabled ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
              data-testid={`feature-card-${id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isEnabled ? 'bg-orange-100' : 'bg-slate-100'}`}>
                    <Icon size={20} className={isEnabled ? 'text-orange-600' : 'text-slate-400'} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-navy-900">{feature.label}</h3>
                    <p className="text-xs text-navy-500 mt-0.5">{feature.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature(id, isEnabled)}
                  disabled={toggling === id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    isEnabled ? 'bg-orange-500' : 'bg-slate-300'
                  } ${toggling === id ? 'opacity-50' : ''}`}
                  data-testid={`feature-toggle-${id}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-700">
        <div className="flex items-start gap-2">
          <Warning size={16} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Disabling features</p>
            <p className="mt-1">When a feature is disabled, it is hidden from the sidebar navigation and its pages become inaccessible. Existing data is preserved and will be available when the feature is re-enabled.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
